/** Scope of Work — deal-drawer section (the v2 editor Daniel uses instead of
 * v1's page). Section tabs with live totals, categories with item tables,
 * add/edit/delete for cats + items, the markup panel, grand totals. Every
 * change updates local state instantly AND queues door ops (offline-safe,
 * per-record — no whole-blob races, no localStorage dependence). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { usePhone } from "../../lib/usePhone";
import type { RecOp } from "../../lib/outbox";
import { ConfirmDelete, deleteUnlocked } from "../common/ConfirmDelete";
import { emptyMaterial, matId, type MaterialCat, type MaterialInput, type MaterialPriceResult } from "../materials/api";
import { MaterialEditor } from "../materials/MaterialsPage";
import {
  SOW_MSG, SOW_ROOMS, SOW_SECTIONS, SOW_UNITS, catTotals, grandTotals, itemMarkup,
  opSowDelCat, opSowDelItem, opSowMeta, opSowPutCat, opSowPutItem,
  propMarkup, sectionTotal, sowId, sowMatsOf, sowMatsBackfill, sowMatsSum, sowSeedMeta, sowWtOf, usd,
  shrinkPhoto, uploadWalkthroughPhoto, deleteWalkthroughPhoto,
  type SowCat, type SowItem, type SowMatLink, type SowMeta, type SowState, type SowWt,
} from "./api";

/** the material-write callback threaded down to the item editor so a scope item
 * can mint a brand-new catalog material inline (not only from the Materials tab). */
type PutMaterialFn = (id: string, input: MaterialInput, keepHistory: { date: string; price: number }[]) => void;

/** onError: hide a broken <img> (v1 hides broken material/photo thumbnails). */
const hideImg = (e: SyntheticEvent<HTMLImageElement>): void => {
  e.currentTarget.style.display = "none";
};

/** Downscale a photo to a small JPEG dataURL (≤360px) — the same budget rule
 * item photos use, shared by the walkthrough check-off camera (2026-08-25). */
function compressPhoto(f: File, done: (dataUrl: string) => void): void {
  const reader = new FileReader();
  reader.onload = () => {
    const src = reader.result;
    if (typeof src !== "string") return;
    const img = new Image();
    img.onload = () => {
      const MAX = 360;
      const scale = Math.min(1, MAX / Math.max(img.width || 1, img.height || 1));
      const w = Math.max(1, Math.round((img.width || 1) * scale));
      const h = Math.max(1, Math.round((img.height || 1) * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { done(src); return; }
      ctx.drawImage(img, 0, 0, w, h);
      done(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.src = src;
  };
  reader.readAsDataURL(f);
}
/** onError for a row thumbnail: hide the whole wrapper so no empty box shows. */
const hideThumb = (e: SyntheticEvent<HTMLImageElement>): void => {
  const w = e.currentTarget.closest(".sowithumb");
  if (w instanceof HTMLElement) w.style.display = "none";
  else e.currentTarget.style.display = "none";
};
/** parse a money/number string like v1's inline setters (strips non-numerics). */
const numOf = (v: string): number => {
  const f = parseFloat(v.replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(f) ? f : 0;
};

/** Expand map that opens ONLY the first category (lowest order) of the active
 * section and collapses every other category. Opening a big scope (20+ cats)
 * fully expanded thrashes layout at the top as lazy thumbnails load — the "first
 * category going nuts" glitch — so we start focused and let the user expand. */
export const onlyFirstOpen = (cats: { id: string; section: string; order: number }[], section: string): Record<string, boolean> => {
  const firstId = cats.filter((c) => c.section === section).sort((a, b) => a.order - b.order)[0]?.id;
  const m: Record<string, boolean> = {};
  for (const c of cats) m[c.id] = c.id === firstId;
  return m;
};
import {
  buildScopeCsv, buildScopePdfHtml, downloadCsv, downloadScopePdf, openPrintable,
  type ExpOpts, type ExpPrices,
} from "./export";
import {
  applyTemplateOps, cloneTplEntry, loadTemplates, saveTemplates, scopeToTemplate,
  scopeTplsAppend, scopeTplsDelete, scopeTplsRename, scopeTplsUpdateEntry, sharedTplIndex, templateToV1Entry,
  type SowTemplate,
} from "./templates";

export function SowSection({
  dealKey,
  fetchSow,
  onOps,
  onForceSave,
  materials,
  onNeedMaterials,
  onPutMaterial,
  materialCats = [],
  onImportUrl,
  sqft,
  onSqft,
  sharedTemplates = [],
  scopeTplsRaw = [],
  onSaveScopeTpls,
  onFlagTask,
  templateMode = false,
  onSaveTemplate,
  token = null,
}: {
  dealKey: string;
  /** Bearer token — walkthrough photos upload to storage (2026-09-06) */
  token?: string | null;
  fetchSow: (dealKey: string) => Promise<SowState | null>;
  onOps: (ops: RecOp[]) => void;
  /** Real Save: flush the outbox, resolve true when nothing is left pending. */
  onForceSave?: () => Promise<boolean>;
  materials: import("../materials/api").CatalogMaterial[];
  onNeedMaterials: () => void;
  /** create a new catalog material inline from the item editor (admin/edit only) */
  onPutMaterial?: PutMaterialFn;
  /** catalog categories — the inline material editor needs one to save */
  materialCats?: MaterialCat[];
  /** product-link import (Amazon / Home Depot / Lowe's) for the inline editor */
  onImportUrl?: (url: string, onWake: () => void) => Promise<MaterialPriceResult>;
  /** property living/gross square footage, shown under the totals (reference) */
  sqft?: { living: number | null; gross: number | null };
  /** save an edited living/gross sqft back to the deal (admin/edit only) */
  onSqft?: (key: "living" | "gross", sqft: number | null) => void;
  /** org-wide templates parsed from v1 SCOPE_TPLS (apply + manage since Phase 28) */
  sharedTemplates?: SowTemplate[];
  /** the RAW org SCOPE_TPLS array (v1 shape) — writes rewrite this wholesale */
  scopeTplsRaw?: unknown[];
  /** Phase 28: persist the whole SCOPE_TPLS array org-wide. Absent = read-only. */
  onSaveScopeTpls?: (all: unknown[]) => void;
  /** v1 scopeFlagSave also unshifts a High-priority "Follow-up: {cat}" deal task */
  onFlagTask?: (catName: string) => void;
  /** Template-editor mode (Settings › Templates): the SAME editor, but the door
   * is a no-op (edits stay in memory) and the toolbar's Save writes the whole
   * template via onSaveTemplate instead of flushing a deal outbox. The
   * apply-a-template modal and deal-only follow-up flags are hidden. */
  templateMode?: boolean;
  onSaveTemplate?: (sow: SowState) => void;
}) {
  const [sow, setSow] = useState<SowState | null>(null);
  const [failed, setFailed] = useState(false);
  const [section, setSection] = useState("interiors");
  const [editItem, setEditItem] = useState<{ catId: string; item: SowItem | null } | null>(null);
  const [confirmDel, setConfirmDel] = useState<{ kind: "cat" | "item"; id: string; label: string } | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const isPhone = usePhone();
  /** phone toolbar sheets: "add" = + Add (category / room / template),
   *  "more" = ⋯ (export / markup / collapse / reset) */
  const [toolSheet, setToolSheet] = useState<null | "add" | "more">(null);
  // on-site walkthrough mode (Tom 2026-08-25) — checklist view over the scope
  const [walkthrough, setWalkthrough] = useState(false);
  /** phone per-category ⋯ sheet — stores the cat ID, looked up fresh at
   *  render so a rename/flag while open can't strand a stale snapshot */
  const [catSheet, setCatSheet] = useState<string | null>(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [addWWOpen, setAddWWOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  // Real Save button (v1 scopeForceSave): "saving" while the outbox drains,
  // then "saved" (green ~2s) or "error" (red, keeps retrying in the background).
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveTimer = useRef<number | null>(null);
  // v1 scopeToggleMarkup: the markup panel is hidden until the toolbar %-chip
  // toggles it (default closed).
  const [markupOpen, setMarkupOpen] = useState(false);
  // v1 clipboard (React state — v2 forbids localStorage), rename/flag modals,
  // per-cat expand state (default EXPANDED: undefined = open, false = collapsed),
  // and the shared drag payload for cat/item reordering.
  const [clip, setClip] = useState<SowItem | null>(null);
  const [renameCat, setRenameCat] = useState<SowCat | null>(null);
  const [flagCat, setFlagCat] = useState<SowCat | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const dragRef = useRef<{ kind: "cat" | "item"; id: string } | null>(null);
  const gen = useRef(0);

  const applyTemplate = (tpl: SowTemplate, mode: "add" | "merge" | "replace") => {
    if (!sow) return;
    const { next, ops } = applyTemplateOps(dealKey, tpl, sow, mode);
    if (ops.length) apply(next, ops);
    setTplOpen(false);
  };

  // v1 scopeForceSave — actually flush the outbox and report the outcome.
  const doForceSave = async () => {
    if (saveTimer.current !== null) { window.clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaveState("saving");
    let ok = false;
    try {
      ok = onForceSave ? await onForceSave() : true;
    } catch {
      ok = false;
    }
    setSaveState(ok ? "saved" : "error");
    saveTimer.current = window.setTimeout(() => setSaveState("idle"), ok ? 2000 : 4000);
  };

  const load = useCallback(async () => {
    const g = ++gen.current;
    setFailed(false);
    const r = await fetchSow(dealKey);
    if (gen.current !== g) return;
    if (r === null) {
      setFailed(true);
      return;
    }
    setSow(r);
    const activeSect = r.meta.activeSection && SOW_SECTIONS.some(([k]) => k === r.meta.activeSection) ? r.meta.activeSection : "interiors";
    setSection(activeSect);
    setExpanded(onlyFirstOpen(r.cats, activeSect)); // open only the first category on load
  }, [dealKey, fetchSow]);

  useEffect(() => {
    setSow(null);
    void load();
  }, [load]);

  // clear the Save-button revert timer on unmount (no setState after teardown).
  useEffect(() => () => { if (saveTimer.current !== null) window.clearTimeout(saveTimer.current); }, []);

  const apply = (next: SowState, ops: RecOp[]) => {
    setSow(next);
    onOps(ops);
  };

  // v1 renderAddCat modal (no browser prompt): name + section.
  const addCatSubmit = (name: string, sect: string) => {
    if (!sow) return;
    const nm = name.trim();
    if (!nm) return;
    const cat: SowCat = {
      id: sowId("sc"),
      name: nm.slice(0, 120),
      section: sect,
      order: sow.cats.filter((c) => c.section === sect).length,
      followUp: false,
      note: "",
    };
    apply({ ...sow, cats: [...sow.cats, cat] }, [opSowPutCat(dealKey, cat)]);
    setAddCatOpen(false);
    if (sect !== section) pickSection(sect);
  };

  // v1 scopeWWSave — append a wood-work item. Unlike v1's ephemeral window._WW,
  // these are real scope items (useCombined, combined = price each) under one
  // auto "WOOD WORK" category in the woodwork section, so they persist through
  // the normal scope door and count toward the section/grand totals.
  const wwAddItem = (name: string, qty: number, price: number) => {
    if (!sow) return;
    const nm = name.trim();
    if (!nm) return;
    const ops: RecOp[] = [];
    let cat = sow.cats.find((c) => c.section === "woodwork");
    let cats = sow.cats;
    if (!cat) {
      cat = { id: sowId("sc"), name: "WOOD WORK", section: "woodwork", order: 0, followUp: false, note: "" };
      cats = [...sow.cats, cat];
      ops.push(opSowPutCat(dealKey, cat));
    }
    const theCat = cat;
    const item: SowItem = {
      id: sowId("si"),
      catId: theCat.id,
      name: nm.slice(0, 120),
      qty,
      unit: "each",
      labor: 0,
      material: 0,
      markupMode: "inherit",
      markupPct: 0,
      useCombined: true,
      combined: price,
      desc: "",
      laborNotes: "",
      materialNotes: "",
      photos: [],
      order: sow.items.filter((i) => i.catId === theCat.id).length,
      isSub: false,
      raw: {},
    };
    ops.push(opSowPutItem(dealKey, item));
    apply({ ...sow, cats, items: [...sow.items, item] }, ops);
    setAddWWOpen(false);
  };

  // v1 resetScope — clear every category + item on this deal (confirm first).
  const resetScope = () => {
    if (!sow) return;
    const ops = [
      ...sow.items.map((i) => opSowDelItem(dealKey, i.id)),
      ...sow.cats.map((c) => opSowDelCat(dealKey, c.id)),
    ];
    apply({ ...sow, cats: [], items: [] }, ops);
    setResetOpen(false);
  };

  const delCat = (cat: SowCat) => {
    if (!sow) return;
    const gone = sow.items.filter((i) => i.catId === cat.id).map((i) => opSowDelItem(dealKey, i.id));
    apply(
      { ...sow, cats: sow.cats.filter((c) => c.id !== cat.id), items: sow.items.filter((i) => i.catId !== cat.id) },
      [...gone, opSowDelCat(dealKey, cat.id)],
    );
    setConfirmDel(null);
  };

  // Walkthrough check-off (Tom 2026-08-25): patch ONLY raw.wt on one item and
  // ship it through the same per-record op the editor uses.
  const patchItemWt = (it: SowItem, wt: SowWt) => {
    if (!sow) return;
    const next: SowItem = {
      ...it,
      raw: { ...it.raw, wt: { ...wt, at: new Date().toISOString().slice(0, 10) } },
    };
    apply({ ...sow, items: sow.items.map((x) => (x.id === it.id ? next : x)) },
          [opSowPutItem(dealKey, next)]);
  };

  const saveItem = (catId: string, prev: SowItem | null, draft: SowItem) => {
    if (!sow) return;
    const it: SowItem = { ...draft, catId, id: prev?.id ?? sowId("si") };
    const items = prev ? sow.items.map((x) => (x.id === prev.id ? it : x)) : [...sow.items, { ...it, order: sow.items.filter((x) => x.catId === catId).length }];
    apply({ ...sow, items }, [opSowPutItem(dealKey, prev ? it : items.find((x) => x.id === it.id)!)]);
    setEditItem(null);
  };

  const delItem = (id: string) => {
    if (!sow) return;
    apply({ ...sow, items: sow.items.filter((i) => i.id !== id) }, [opSowDelItem(dealKey, id)]);
    setConfirmDel(null);
  };

  const setMarkup = (mode: "inherit" | "custom" | "off", pct?: number) => {
    if (!sow) return;
    const meta = { ...sow.meta, markupMode: mode, ...(pct !== undefined ? { markupPct: pct } : {}) };
    apply({ ...sow, meta }, [opSowMeta(dealKey, meta)]);
  };

  const pickSection = (k: string) => {
    setSection(k);
    if (!sow) return;
    const meta = { ...sow.meta, activeSection: k };
    apply({ ...sow, meta }, [opSowMeta(dealKey, meta)]);
  };

  // v1 scopeToggleCat — default open; store `false` only when collapsed.
  const toggleCat = (id: string) => setExpanded((e) => ({ ...e, [id]: e[id] === false }));
  // Expand-all clears the collapse map (undefined = open); collapse-all marks
  // every category false. Both are whole-scope, across sections.
  const expandAll = () => setExpanded({});
  const collapseAll = () => setExpanded(sow ? Object.fromEntries(sow.cats.map((c) => [c.id, false])) : {});
  // are any categories currently open? (drives the single toggle's label/action)
  const anyOpen = !!sow && sow.cats.some((c) => expanded[c.id] !== false);

  // v1 scopeSetQty/Unit/Labor/Material/Combined — patch one item, queue its op.
  const patchItem = (it: SowItem, patch: Partial<SowItem>) => {
    if (!sow) return;
    const next: SowItem = { ...it, ...patch };
    apply({ ...sow, items: sow.items.map((x) => (x.id === it.id ? next : x)) }, [opSowPutItem(dealKey, next)]);
  };

  /** Phone editor's "Duplicate": copy-and-paste as ONE act. The desktop ⧉ only
   *  copies to the clipboard and relies on you noticing the 📋 appear on some
   *  category header — on a phone that second step is invisible. */
  const duplicateItem = (it: SowItem) => {
    if (!sow) return;
    const snap = JSON.parse(JSON.stringify(it)) as SowItem;
    snap.id = sowId("si");
    snap.order = sow.items.filter((i) => i.catId === it.catId).length;
    apply({ ...sow, items: [...sow.items, snap] }, [opSowPutItem(dealKey, snap)]);
  };

  // v1 scopeCopy — deep copy with id cleared; scopePaste — fresh id + next order.
  const copyItem = (it: SowItem) => {
    const snap = JSON.parse(JSON.stringify(it)) as SowItem;
    snap.id = "";
    setClip(snap);
  };
  const pasteItem = (catId: string) => {
    if (!sow || !clip) return;
    const it: SowItem = { ...clip, id: sowId("si"), catId, order: sow.items.filter((i) => i.catId === catId).length };
    apply({ ...sow, items: [...sow.items, it] }, [opSowPutItem(dealKey, it)]);
  };

  // v1 scopeRenameCat.
  const doRename = (name: string) => {
    if (!sow || !renameCat) return;
    const nm = name.trim();
    if (!nm) return;
    const next: SowCat = { ...renameCat, name: nm };
    apply({ ...sow, cats: sow.cats.map((c) => (c.id === renameCat.id ? next : c)) }, [opSowPutCat(dealKey, next)]);
    setRenameCat(null);
  };

  // v1 scopeFlag/scopeFlagSave. Flagging sets followUp + note + red styling AND
  // unshifts a High-priority "Follow-up: {cat}" deal task via onFlagTask (v1
  // scopeFlagSave). Resolving only clears the flag/note — it never adds a task.
  const resolveFlag = (cat: SowCat) => {
    if (!sow) return;
    const next: SowCat = { ...cat, followUp: false, note: "" };
    apply({ ...sow, cats: sow.cats.map((c) => (c.id === cat.id ? next : c)) }, [opSowPutCat(dealKey, next)]);
  };
  const saveFlag = (note: string) => {
    if (!sow || !flagCat) return;
    const next: SowCat = { ...flagCat, followUp: true, note: note.trim() };
    apply({ ...sow, cats: sow.cats.map((c) => (c.id === flagCat.id ? next : c)) }, [opSowPutCat(dealKey, next)]);
    onFlagTask?.(flagCat.name); // v1 scopeFlagSave: High-priority "Follow-up: {cat}" task
    setFlagCat(null);
  };

  // v1 scopeDrop('cat') — reorder cats in the active section, queue moved cats.
  const reorderCats = (dragId: string, dropId: string) => {
    if (!sow) return;
    const list = sow.cats.filter((c) => c.section === section).sort((a, b) => a.order - b.order);
    const from = list.findIndex((c) => c.id === dragId);
    const to = list.findIndex((c) => c.id === dropId);
    if (from < 0 || to < 0 || from === to) return;
    const moved = list[from];
    if (!moved) return;
    const reordered = list.slice();
    reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const withOrder = reordered.map((cat, i) => ({ cat, order: i }));
    const changed = withOrder.filter(({ cat, order }) => cat.order !== order);
    if (!changed.length) return;
    const orderOf = new Map(withOrder.map((x) => [x.cat.id, x.order] as const));
    const cats = sow.cats.map((c) => {
      const o = orderOf.get(c.id);
      return o !== undefined && o !== c.order ? { ...c, order: o } : c;
    });
    apply({ ...sow, cats }, changed.map(({ cat, order }) => opSowPutCat(dealKey, { ...cat, order })));
  };

  // v1 scopeDrop('item') — reorder items within their category, queue moved items.
  const reorderItems = (dragId: string, dropId: string) => {
    if (!sow) return;
    const drag = sow.items.find((i) => i.id === dragId);
    const drop = sow.items.find((i) => i.id === dropId);
    if (!drag || !drop || drag.catId !== drop.catId) return;
    const list = sow.items.filter((i) => i.catId === drag.catId).sort((a, b) => a.order - b.order);
    const from = list.findIndex((i) => i.id === dragId);
    const to = list.findIndex((i) => i.id === dropId);
    if (from < 0 || to < 0 || from === to) return;
    const moved = list[from];
    if (!moved) return;
    const reordered = list.slice();
    reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const withOrder = reordered.map((it, i) => ({ it, order: i }));
    const changed = withOrder.filter(({ it, order }) => it.order !== order);
    if (!changed.length) return;
    const orderOf = new Map(withOrder.map((x) => [x.it.id, x.order] as const));
    const items = sow.items.map((i) => {
      const o = orderOf.get(i.id);
      return o !== undefined && o !== i.order ? { ...i, order: o } : i;
    });
    apply({ ...sow, items }, changed.map(({ it, order }) => opSowPutItem(dealKey, { ...it, order })));
  };

  const onCatDragStart = (id: string) => { dragRef.current = { kind: "cat", id }; };
  const onCatDrop = (id: string) => { const d = dragRef.current; dragRef.current = null; if (d && d.kind === "cat") reorderCats(d.id, id); };
  const onItemDragStart = (id: string) => { dragRef.current = { kind: "item", id }; };
  const onItemDrop = (id: string) => { const d = dragRef.current; dragRef.current = null; if (d && d.kind === "item") reorderItems(d.id, id); };

  const grand = useMemo(() => (sow ? grandTotals(sow) : null), [sow]);

  if (failed) {
    return (
      <div className="sow">
        <div className="pill stale">{SOW_MSG.loadFail}</div>
        <button className="btn" onClick={() => void load()}>Try again</button>
      </div>
    );
  }
  if (!sow) return <div className="sownote">Loading scope…</div>;

  const cats = sow.cats.filter((c) => c.section === section).sort((a, b) => a.order - b.order);

  const followUps = sow.cats.filter((c) => c.followUp);

  // v1 scopeWoodwork: the woodwork section is a dedicated calculator over the
  // combined-price items in that section (all cats there, normally the one auto
  // "WOOD WORK"), not the generic category cards.
  const wwCatIds = new Set(sow.cats.filter((c) => c.section === "woodwork").map((c) => c.id));
  const wwItems = sow.items.filter((i) => wwCatIds.has(i.catId) && !i.isSub).sort((a, b) => a.order - b.order);

  return (
    <div className="sow">
      {/* v1 SOW header: title + counts + TOTAL BUDGET, and the Labor / Material /
          Markup tiles. */}
      <div className="sowhead">
        <div className="sowheadl">
          <span className="sowheadic">🔨</span>
          <div>
            <strong>Scope of Work</strong>
            <span className="sub">{sow.cats.length} categor{sow.cats.length === 1 ? "y" : "ies"} · {sow.items.length} item{sow.items.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="sowheadr">
          <span className="sowheadtot">{usd(grand?.total ?? 0)}</span>
          <span className="sub">TOTAL BUDGET</span>
        </div>
      </div>
      <div className="sowtiles">
        {/* variant classes are t-prefixed ON PURPOSE: bare "mat" collided with the
            Materials PAGE class (.mat{gap:12px}) and pushed this tile's number down */}
        <div className="sowtile tlab"><span>LABOR</span><b>{usd(grand?.labor ?? 0)}</b></div>
        <div className="sowtile tmat"><span>MATERIAL</span><b>{usd(grand?.material ?? 0)}</b></div>
        <div className="sowtile tmk"><span>MARKUP</span><b>{grand && grand.markup ? "+" + usd(grand.markup) : "—"}</b></div>
      </div>
      {sqft ? (
        <div className="sowsqft">
          <SqftCell label="Living area" value={sqft.living} onCommit={onSqft ? (v) => onSqft("living", v) : null} />
          <span className="sowsqftsep">·</span>
          <SqftCell label="Gross area" value={sqft.gross} onCommit={onSqft ? (v) => onSqft("gross", v) : null} />
        </div>
      ) : null}
      {followUps.length ? (
        <div className="sowfollowup">
          <strong>⚠ {followUps.length} categor{followUps.length === 1 ? "y needs" : "ies need"} follow-up</strong>
          <div className="sowfulist">
            {followUps.map((c) => (
              <div key={c.id} className="sowfuitem">
                <span className="sowfuname">{c.name}</span>
                {c.note ? <span className="sowfunote"> — {c.note}</span> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Phone: one scrolling row, sections with money first, $0 ones dimmed
          at the back (stable sort keeps declaration order inside each group).
          Six chips with totals wrapped onto two rows and pushed the toolbar
          down; the $0 ones were taking front-row seats to say "nothing here". */}
      <div className="sowtabs">
        {(isPhone
          ? [...SOW_SECTIONS].sort((a, b) =>
              (sectionTotal(sow, b[0]) > 0 ? 1 : 0) - (sectionTotal(sow, a[0]) > 0 ? 1 : 0))
          : SOW_SECTIONS
        ).map(([k, label]) => (
          <button
            key={k}
            className={"sowtab" + (section === k ? " on" : "") + (isPhone && sectionTotal(sow, k) <= 0 ? " zero" : "")}
            onClick={() => pickSection(k)}
          >
            {label} <span className="sowtabtotal">{usd(sectionTotal(sow, k))}</span>
          </button>
        ))}
      </div>

      {section === "woodwork" ? (
        <WoodworkPanel
          items={wwItems}
          onAdd={() => setAddWWOpen(true)}
          onPatch={patchItem}
          onDelete={delItem}
        />
      ) : (
      <>
      {/* v1 SOW toolbar. Phone (Tom 2026-07-30, "so many clicks and things are
          so small to press on"): eight buttons wrapped onto FOUR rows. Now
          three — Save, + Add, ⋯ — and the other six live in two sheets with
          48px rows. Desktop keeps the full bar. */}
      <div className={"sowtoolbar" + (isPhone ? " phone3" : "")}>
        {templateMode ? (
          <button className="btn primary" title="Save this template for the whole team" onClick={() => onSaveTemplate?.(sow)}>💾 Save template</button>
        ) : (
        <button
          className={"btn primary" + (saveState === "saved" ? " saved" : saveState === "error" ? " notsaved" : "")}
          disabled={saveState === "saving"}
          onClick={() => void doForceSave()}
        >
          {saveState === "saving" ? "Saving…"
            : saveState === "saved" ? "Saved ✓"
            : saveState === "error" ? (isPhone ? "⚠ Not saved" : "⚠ Not saved — will keep retrying")
            : "💾 Save"}
        </button>
        )}
        {sow.items.length && !templateMode ? (
          <button
            className={"btn sowwalkbtn" + (walkthrough ? " primary" : "")}
            title="On-site checklist: check off what the contractor finished"
            onClick={() => setWalkthrough((v) => !v)}
          >
            {walkthrough ? "✎ Back to editing" : "☑ Walkthrough"}
          </button>
        ) : null}
        {isPhone ? (
          <>
            <button className="btn" onClick={() => setToolSheet("add")}>+ Add</button>
            <button className="btn sowmorebtn" aria-label="More scope actions" onClick={() => setToolSheet("more")}>⋯</button>
          </>
        ) : (
          <>
        <button className="btn" onClick={() => setAddCatOpen(true)}>+ Add Category</button>
        <button className="btn" onClick={() => setAddRoomOpen(true)}>🏠 Add Room</button>
        {templateMode ? null : <button className="btn" onClick={() => setTplOpen(true)}>▣ Templates</button>}
        {sow.items.length ? <button className="btn" onClick={() => setExportOpen(true)}>⤓ Export</button> : null}
        <button
          className={"btn mk" + (markupOpen ? " on" : "")}
          title="Toggle material markup"
          onClick={() => setMarkupOpen((v) => !v)}
        >
          Markup {propMarkup(sow.meta)}%
        </button>
        {sow.cats.length > 1 ? (
          <button className="btn" title={anyOpen ? "Collapse every category" : "Expand every category"} onClick={() => (anyOpen ? collapseAll() : expandAll())}>
            {anyOpen ? "⊟ Collapse all" : "⊞ Expand all"}
          </button>
        ) : null}
        {sow.cats.length || sow.items.length ? <button className="btn danger" onClick={() => setResetOpen(true)}>↺ Reset Scope</button> : null}
          </>
        )}
      </div>
      {toolSheet ? (
        <div className="pdmscrim" onClick={() => setToolSheet(null)}>
          <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
            {toolSheet === "add" ? (
              <>
                <div className="pdmhd">Add to scope</div>
                <button className="pdmitem" onClick={() => { setToolSheet(null); setAddCatOpen(true); }}>+ Add category</button>
                <button className="pdmitem" onClick={() => { setToolSheet(null); setAddRoomOpen(true); }}>🏠 Add room</button>
                {templateMode ? null : (
                  <button className="pdmitem" onClick={() => { setToolSheet(null); setTplOpen(true); }}>▣ From template…</button>
                )}
              </>
            ) : (
              <>
                <div className="pdmhd">Scope actions</div>
                {sow.items.length ? (
                  <button className="pdmitem" onClick={() => { setToolSheet(null); setExportOpen(true); }}>⤓ Export</button>
                ) : null}
                <button className="pdmitem" onClick={() => { setToolSheet(null); setMarkupOpen(true); }}>
                  Markup — {propMarkup(sow.meta)}%
                </button>
                {sow.cats.length > 1 ? (
                  <button className="pdmitem" onClick={() => { setToolSheet(null); (anyOpen ? collapseAll() : expandAll()); }}>
                    {anyOpen ? "⊟ Collapse all" : "⊞ Expand all"}
                  </button>
                ) : null}
                {sow.cats.length || sow.items.length ? (
                  <button className="pdmitem danger" onClick={() => { setToolSheet(null); setResetOpen(true); }}>↺ Reset scope</button>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      {markupOpen ? (
        <div className="sowmarkup">
          <span className="sowmklbl">Material markup:</span>
          {([["inherit", `Default (${sow.meta.defaultMarkup}%)`], ["custom", "Custom"], ["off", "Off"]] as [
            "inherit" | "custom" | "off", string,
          ][]).map(([v, l]) => (
            <button key={v} className={"sowmkbtn" + (sow.meta.markupMode === v ? " on" : "")} onClick={() => setMarkup(v)}>
              {l}
            </button>
          ))}
          {sow.meta.markupMode === "custom" ? (
            <input
              className="sowmkpct"
              type="number"
              value={sow.meta.markupPct}
              onChange={(e) => setMarkup("custom", parseFloat(e.target.value) || 0)}
            />
          ) : null}
          <span className="sowmknow">→ {propMarkup(sow.meta)}%</span>
        </div>
      ) : null}

      {walkthrough ? (
        <WalkthroughView sow={sow} materials={materials} onPatch={patchItemWt} token={token} dealKey={dealKey} />
      ) : cats.length === 0 ? (
        <div className="sowempty">
          <span className="sowemptyic">🗂</span>
          <strong>No categories yet</strong>
          <span className="sub">{sow.cats.length ? SOW_MSG.emptySection : "Add a category to start your scope"}</span>
          <button className="btn primary" onClick={() => setAddCatOpen(true)}>+ Add First Category</button>
        </div>
      ) : (
        <div className="sowcats">
          {cats.map((cat) => {
            const t = catTotals(sow.items, cat.id, sow.meta);
            const rows = sow.items.filter((i) => i.catId === cat.id && !i.isSub).sort((a, b) => a.order - b.order);
            const open = expanded[cat.id] !== false;
            return (
              <section
                key={cat.id}
                className={"sowcard" + (cat.followUp ? " followup" : "")}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onCatDrop(cat.id)}
              >
                <div className="sowcardhead">
                  {/* grip is a desktop tool — on phones reordering lives in the
                      ⋯ sheet as Move up/down, where a thumb can actually do it */}
                  {isPhone ? null : (
                  <span className="sowgrip" draggable onDragStart={() => onCatDragStart(cat.id)} title="Drag to reorder">⠿</span>
                  )}
                  <button className="sowcardtoggle" onClick={() => toggleCat(cat.id)}>
                    <span className="sowchev">{cat.followUp ? "⚑" : open ? "▾" : "▸"}</span>
                    <span className="sowcardnamewrap">
                      <span className="sowcardname">
                        {cat.name}
                        {cat.followUp ? <span className="sowpill followup">Follow-up</span> : null}
                      </span>
                      <span className="sowcardsub">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
                      {cat.followUp && cat.note ? <span className="sowcardnote">⚑ {cat.note}</span> : null}
                    </span>
                  </button>
                  <div className="sowcardtot">
                    <div className="sowcardtotb">{usd(t.total)}</div>
                    <div className="sowcardtotsub">L {usd(t.labor)} · M {usd(t.material)}</div>
                  </div>
                  {/* Phone: five ~24px icons become one 44px ⋯ (Tom: "things
                      are so small to press on"). Desktop keeps the icon row. */}
                  {isPhone ? (
                    <button className="sowcatmore" aria-label={`Actions for ${cat.name}`} onClick={() => setCatSheet(cat.id)}>⋯</button>
                  ) : (
                  <div className="sowcardacts">
                    <button className="sowcardact" title="Add item" onClick={() => setEditItem({ catId: cat.id, item: null })}>＋</button>
                    {clip ? <button className="sowcardact paste" title="Paste item" onClick={() => pasteItem(cat.id)}>📋</button> : null}
                    <button className="sowcardact" title="Rename" onClick={() => setRenameCat(cat)}>✎</button>
                    <button
                      className={"sowcardact " + (cat.followUp ? "resolve" : "flag")}
                      title={cat.followUp ? "Resolve follow-up" : "Flag for follow-up"}
                      onClick={() => (cat.followUp ? resolveFlag(cat) : setFlagCat(cat))}
                    >
                      {cat.followUp ? "✓" : "⚑"}
                    </button>
                    <button className="sowcardact del" title="Delete" onClick={() => setConfirmDel({ kind: "cat", id: cat.id, label: cat.name })}>🗑</button>
                  </div>
                  )}
                </div>
                {open ? (
                  <div className="sowcardbody">
                    {rows.length ? (
                      rows.map((it) => (
                        <ItemRow
                          key={it.id}
                          it={it}
                          meta={sow.meta}
                          materials={materials}
                          onPatch={(patch) => patchItem(it, patch)}
                          onEdit={() => setEditItem({ catId: cat.id, item: it })}
                          onCopy={() => copyItem(it)}
                          onDelete={() => setConfirmDel({ kind: "item", id: it.id, label: it.name })}
                          onDragStart={() => onItemDragStart(it.id)}
                          onDrop={() => onItemDrop(it.id)}
                        />
                      ))
                    ) : (
                      <div className="sowcardempty">No items yet.</div>
                    )}
                    <button className="sowadditem" onClick={() => setEditItem({ catId: cat.id, item: null })}>＋ Add Item</button>
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      {cats.length ? (
        <div className="sowfoot">
          <button className="btn" onClick={() => setAddCatOpen(true)}>+ Add Category</button>
        </div>
      ) : null}
      </>
      )}
      <p className="sowv1note">{SOW_MSG.v1Note}</p>

      {(() => {
        if (!catSheet) return null;
        const cat = cats.find((c) => c.id === catSheet);
        if (!cat) return null;                    // deleted while open
        const idx = cats.findIndex((c) => c.id === cat.id);
        const close = () => setCatSheet(null);
        return (
          <div className="pdmscrim" onClick={close}>
            <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
              <div className="pdmhd">{cat.name}</div>
              <button className="pdmitem" onClick={() => { close(); setEditItem({ catId: cat.id, item: null }); }}>＋ Add item</button>
              {clip ? <button className="pdmitem" onClick={() => { close(); pasteItem(cat.id); }}>📋 Paste item</button> : null}
              <button className="pdmitem" onClick={() => { close(); setRenameCat(cat); }}>✎ Rename</button>
              <button className="pdmitem" onClick={() => { close(); cat.followUp ? resolveFlag(cat) : setFlagCat(cat); }}>
                {cat.followUp ? "✓ Resolve follow-up" : "⚑ Flag for follow-up"}
              </button>
              {idx > 0 ? (
                <button className="pdmitem" onClick={() => { close(); reorderCats(cat.id, cats[idx - 1]!.id); }}>↑ Move up</button>
              ) : null}
              {idx < cats.length - 1 ? (
                <button className="pdmitem" onClick={() => { close(); reorderCats(cat.id, cats[idx + 1]!.id); }}>↓ Move down</button>
              ) : null}
              <button className="pdmitem danger" onClick={() => { close(); setConfirmDel({ kind: "cat", id: cat.id, label: cat.name }); }}>🗑 Delete category</button>
            </div>
          </div>
        );
      })()}

      {editItem ? (
        <ItemModal
          initial={editItem.item}
          materials={materials}
          onNeedMaterials={onNeedMaterials}
          {...(onPutMaterial ? { onPutMaterial } : {})}
          materialCats={materialCats}
          {...(onImportUrl ? { onImportUrl } : {})}
          onCancel={() => setEditItem(null)}
          onSave={(draft) => saveItem(editItem.catId, editItem.item, draft)}
          {...(editItem.item ? {
            onDuplicate: () => { const it = editItem.item as SowItem; setEditItem(null); duplicateItem(it); },
            onDelete: () => { const it = editItem.item as SowItem; setEditItem(null); setConfirmDel({ kind: "item", id: it.id, label: it.name }); },
          } : {})}
        />
      ) : null}

      {exportOpen ? (
        <ExportModal sow={sow} addr={dealKey} materials={materials} onClose={() => setExportOpen(false)} />
      ) : null}

      {tplOpen && !templateMode ? (
        <TemplatesModal sow={sow} sharedTemplates={sharedTemplates} scopeTplsRaw={scopeTplsRaw} {...(onSaveScopeTpls ? { onSaveScopeTpls } : {})} onApply={applyTemplate} onClose={() => setTplOpen(false)} />
      ) : null}

      {addCatOpen ? (
        <AddCatModal section={section} onCancel={() => setAddCatOpen(false)} onAdd={addCatSubmit} />
      ) : null}

      {addRoomOpen ? (
        <AddRoomModal section={section} onCancel={() => setAddRoomOpen(false)} onAdd={(name, sect) => { setAddRoomOpen(false); addCatSubmit(name, sect); }} />
      ) : null}

      {addWWOpen ? (
        <AddWoodworkModal onCancel={() => setAddWWOpen(false)} onAdd={wwAddItem} />
      ) : null}

      {renameCat ? (
        <RenameCatModal cat={renameCat} onCancel={() => setRenameCat(null)} onSave={doRename} />
      ) : null}

      {flagCat ? (
        <FlagCatModal cat={flagCat} onCancel={() => setFlagCat(null)} onSave={saveFlag} />
      ) : null}

      {/* v1 typed-delete (scopeReset / scopeDeleteItem / scopeDeleteCat) — the
          type-"delete" modal guards all three, with v1's exact wording. */}
      {resetOpen ? (
        <ResetScopeConfirm
          cats={sow.cats.length}
          items={sow.items.filter((i) => !i.isSub).length}
          total={grand?.total ?? 0}
          onCancel={() => setResetOpen(false)}
          onReset={resetScope}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmDelete
          message={confirmDel.kind === "cat" ? "Delete this category and all its items?" : "Delete this item?"}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            if (confirmDel.kind === "item") delItem(confirmDel.id);
            else {
              const cat = sow.cats.find((c) => c.id === confirmDel.id);
              if (cat) delCat(cat);
              else setConfirmDel(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** v1 renderAddCat — Category Name + Section, no browser prompt. */
function AddCatModal({ section, onCancel, onAdd }: {
  section: string;
  onCancel: () => void;
  onAdd: (name: string, section: string) => void;
}) {
  const [name, setName] = useState("");
  const [sect, setSect] = useState(section);
  const submit = () => { if (name.trim()) onAdd(name, sect); };
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Add Category</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <label className="lbl">Category Name</label>
          <input
            className="moneyinput"
            autoFocus
            placeholder="e.g. KITCHEN, EXTERIOR"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <label className="lbl" style={{ marginTop: 10 }}>Section</label>
          <select className="moneyinput" value={sect} onChange={(e) => setSect(e.target.value)}>
            {SOW_SECTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <div className="cmpmodalacts" style={{ marginTop: 14 }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn primary" onClick={submit} disabled={!name.trim()}>Add Category</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** v1 scopeAddRoom — room preset <select> + "or custom name" + section (no
 * browser prompt). Custom name overrides the preset; reuses Add Category's
 * cat-creation path via onAdd. */
function AddRoomModal({ section, onCancel, onAdd }: {
  section: string;
  onCancel: () => void;
  onAdd: (name: string, section: string) => void;
}) {
  const [room, setRoom] = useState(SOW_ROOMS[0] ?? "");
  const [custom, setCustom] = useState("");
  const [sect, setSect] = useState(section);
  const name = custom.trim() || room;
  const submit = () => { if (name.trim()) onAdd(name, sect); };
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>🏠 Add Room</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <label className="lbl">Select Room</label>
          <select className="moneyinput" value={room} onChange={(e) => setRoom(e.target.value)}>
            {SOW_ROOMS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="lbl" style={{ marginTop: 10 }}>Or custom name</label>
          <input
            className="moneyinput"
            placeholder="Custom room name"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <label className="lbl" style={{ marginTop: 10 }}>Section</label>
          <select className="moneyinput" value={sect} onChange={(e) => setSect(e.target.value)}>
            {SOW_SECTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <div className="cmpmodalacts" style={{ marginTop: 14 }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn primary" onClick={submit} disabled={!name.trim()}>Add Room</button>
          </div>
        </div>
      </div>
    </div>
  );
}


/** Full-size walkthrough photo viewer (2026-09-06): swipe/arrow through an
 * item's photos, remove the one on screen. Taps on a thumb used to DELETE the
 * photo — with dozens per item that had to become "view". */
export function PhotoViewer({ title, photos, idx, onIdx, onClose, onRemove }: {
  title: string;
  photos: string[];
  idx: number;
  onIdx: (i: number) => void;
  onClose: () => void;
  onRemove: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setConfirm(false); }, [idx]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && idx < photos.length - 1) onIdx(idx + 1);
      else if (e.key === "ArrowLeft" && idx > 0) onIdx(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, photos.length, onIdx, onClose]);
  const touch = useRef<number | null>(null);
  return (
    <div className="modalscrim sowpv" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { touch.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        const x0 = touch.current; touch.current = null;
        const x1 = e.changedTouches[0]?.clientX;
        if (x0 === null || x1 === undefined) return;
        if (x1 - x0 < -40 && idx < photos.length - 1) onIdx(idx + 1);
        if (x1 - x0 > 40 && idx > 0) onIdx(idx - 1);
      }}>
      <div className="sowpv-box" role="dialog" aria-modal="true" aria-label={"Photos — " + title}>
        <div className="sowpv-hd">
          <span className="sowpv-title">{title}</span>
          <span className="sub">{idx + 1} / {photos.length}</span>
          <button className="modalx" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="sowpv-img">
          <button className="sowpv-nav" disabled={idx === 0} onClick={() => onIdx(idx - 1)} aria-label="Previous">‹</button>
          <img src={photos[idx]} alt="" />
          <button className="sowpv-nav" disabled={idx >= photos.length - 1} onClick={() => onIdx(idx + 1)} aria-label="Next">›</button>
        </div>
        <div className="sowpv-strip">
          {photos.map((p, i) => (
            <img key={i} src={p} alt="" loading="lazy" className={i === idx ? "on" : ""} onClick={() => onIdx(i)} />
          ))}
        </div>
        <div className="sowpv-acts">
          <a className="btn slim" href={photos[idx]} target="_blank" rel="noreferrer">Open full size</a>
          {confirm ? (
            <>
              <span className="sub">Remove this photo?</span>
              <button className="btn slim danger" onClick={onRemove}>Yes, remove</button>
              <button className="btn slim" onClick={() => setConfirm(false)}>Keep</button>
            </>
          ) : (
            <button className="btn slim" onClick={() => setConfirm(true)}>Remove</button>
          )}
        </div>
      </div>
    </div>
  );
}

/** WALKTHROUGH (Tom 2026-08-25, approved mock): the on-site checklist over the
 * whole scope. Every item gets a tappable status (Not started → 50% → Done,
 * cycling), a fine-grained % box, check-off photos straight from the phone
 * camera, and a flag note for anything blocked. The summary shows items done,
 * EARNED VALUE (Σ item total × %) against the budget — the number that matters
 * for contractor draws — and flagged issues. Everything saves per item through
 * the normal scope door, so Daniel sees the same progress live. */
function WalkthroughView({ sow, materials, onPatch, token = null, dealKey = "" }: {
  sow: SowState;
  materials: import("../materials/api").CatalogMaterial[];
  onPatch: (it: SowItem, wt: SowWt) => void;
  token?: string | null;
  dealKey?: string;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pendingRef = useRef<SowItem | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  // photo uploads in flight per item (count) + the last upload problem
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  // the viewer: which item's photos, which index
  const [viewer, setViewer] = useState<{ itemId: string; idx: number } | null>(null);
  // the freshest wt per item — uploads finish out of order, so each one
  // patches on top of what's CURRENT, never a stale snapshot
  const latestWt = useRef<Map<string, SowWt>>(new Map());
  const items = sow.items.filter((i) => !i.isSub);
  // once nothing is uploading for an item, the loaded scope is the truth
  // again (another device may have changed it) — drop our in-flight copy
  useEffect(() => {
    for (const id of [...latestWt.current.keys()]) if (!uploading[id]) latestWt.current.delete(id);
  }, [sow, uploading]);
  const line = (it: SowItem): number => {
    const q = it.qty || 0;
    const mk = it.useCombined ? 0 : itemMarkup(it, sow.meta);
    return it.useCombined ? q * (it.combined || 0) : q * it.labor + q * it.material * (1 + mk / 100);
  };
  /** the item's CURRENT walkthrough state: an in-flight copy while photos
   *  are uploading (they finish out of order), else what the scope holds */
  const wtOf = (it: SowItem): SowWt => latestWt.current.get(it.id) ?? sowWtOf(it.raw);
  const curWt = wtOf;
  /** every walkthrough write goes through here so a % change made while a
   *  photo is still uploading is never overwritten by that upload */
  const commitWt = (it: SowItem, next: SowWt) => {
    if (uploading[it.id]) latestWt.current.set(it.id, next);
    onPatch(it, next);
  };
  const budget = items.reduce((s, i) => s + line(i), 0);
  const earned = items.reduce((s, i) => s + line(i) * (wtOf(i).pct / 100), 0);
  const doneN = items.filter((i) => wtOf(i).pct >= 100).length;
  const flagged = items.filter((i) => wtOf(i).note).length;
  const pctAll = budget > 0 ? Math.round((earned / budget) * 100) : 0;

  const cycle = (it: SowItem) => {
    const wt = wtOf(it);
    commitWt(it, { ...wt, pct: wt.pct >= 100 ? 0 : wt.pct > 0 ? 100 : 50 });
  };
  const setPct = (it: SowItem, v: string) => {
    const n = Math.max(0, Math.min(100, Math.round(parseFloat(v) || 0)));
    const wt = wtOf(it);
    if (n !== wt.pct) commitWt(it, { ...wt, pct: n });
  };
  const askPhoto = (it: SowItem) => { pendingRef.current = it; fileRef.current?.click(); };
  const addPhotos = (it: SowItem, urls: string[]) => {
    if (!urls.length) return;
    const wt = curWt(it);
    const next: SowWt = { ...wt, photos: [...(wt.photos ?? []), ...urls] };
    latestWt.current.set(it.id, next);   // more may still be in flight
    onPatch(it, next);
  };
  /** As many as we want (Tom 2026-09-06): every picked photo is shrunk to
   *  1280px and uploaded to storage; the item keeps only the URL. With no
   *  connection the old inline path still works (a small thumb, budget-bound). */
  const onFiles = (files: FileList | null) => {
    const it = pendingRef.current;
    pendingRef.current = null;
    if (!it || !files || !files.length) return;
    const list = Array.from(files);
    setPhotoErr(null);
    setUploading((u) => ({ ...u, [it.id]: (u[it.id] ?? 0) + list.length }));
    const done = () => setUploading((u) => {
      const n = (u[it.id] ?? 1) - 1;
      const next = { ...u };
      if (n <= 0) delete next[it.id]; else next[it.id] = n;
      return next;
    });
    for (const f of list) {
      void (async () => {
        try {
          if (token && typeof navigator !== "undefined" && navigator.onLine !== false) {
            const blob = await shrinkPhoto(f);
            const url = await uploadWalkthroughPhoto(token, dealKey, it.id, blob);
            addPhotos(it, [url]);
          } else {
            await new Promise<void>((res) => compressPhoto(f, (u) => { addPhotos(it, [u]); res(); }));
          }
        } catch (e) {
          setPhotoErr(`Photo didn't upload (${(e as Error)?.message ?? "error"}) — check the connection and try again.`);
        } finally {
          done();
        }
      })();
    }
  };
  const delPhoto = (it: SowItem, idx: number) => {
    const wt = curWt(it);
    const gone = (wt.photos ?? [])[idx];
    const next: SowWt = { ...wt, photos: (wt.photos ?? []).filter((_, i) => i !== idx) };
    if (!next.photos?.length) delete next.photos;
    latestWt.current.set(it.id, next);
    onPatch(it, next);
    if (gone) void deleteWalkthroughPhoto(token, gone);
  };
  const saveNote = (it: SowItem) => {
    const wt = wtOf(it);
    const t = noteText.trim();
    const next: SowWt = { ...wt };
    if (t) next.note = t;
    else delete next.note;
    commitWt(it, next);
    setNoteFor(null);
    setNoteText("");
  };

  const sectIdx = (k: string): number => {
    const i = SOW_SECTIONS.findIndex(([s]) => s === k);
    return i < 0 ? SOW_SECTIONS.length : i;
  };
  const sectLabel = (k: string): string => SOW_SECTIONS.find(([s]) => s === k)?.[1] ?? k;
  const cats = [...sow.cats]
    .filter((c) => items.some((i) => i.catId === c.id))
    .sort((a, b) => sectIdx(a.section) - sectIdx(b.section) || a.order - b.order);

  return (
    <div className="sowwt">
      <input ref={fileRef} className="sowhiddenfile" type="file" accept="image/*" capture="environment" multiple onChange={(e) => { onFiles(e.target.files); e.currentTarget.value = ""; }} />
      <div className="sowwt-sum">
        <div className="sowwt-kpi"><span className="lbl">Items done</span><b>{doneN} of {items.length}</b></div>
        <div className="sowwt-kpi"><span className="lbl">Earned value</span><b>{usd(earned)}</b><span className="lbl">of {usd(budget)} · {pctAll}%</span></div>
        <div className="sowwt-kpi"><span className="lbl">Flagged issues</span><b>{flagged}</b></div>
      </div>
      <div className="sowwt-bar"><div className="sowwt-fill" style={{ width: pctAll + "%" }} /></div>
      {photoErr ? <div className="caerr" role="alert">{photoErr}</div> : null}
      {viewer ? (() => {
        const it = items.find((x) => x.id === viewer.itemId);
        const ph = it ? (curWt(it).photos ?? []) : [];
        if (!it || !ph.length) return null;
        const idx = Math.max(0, Math.min(viewer.idx, ph.length - 1));
        return (
          <PhotoViewer
            title={it.name}
            photos={ph}
            idx={idx}
            onIdx={(i) => setViewer({ itemId: it.id, idx: i })}
            onClose={() => setViewer(null)}
            onRemove={() => { delPhoto(it, idx); if (ph.length <= 1) setViewer(null); else setViewer({ itemId: it.id, idx: Math.min(idx, ph.length - 2) }); }}
          />
        );
      })() : null}
      {cats.map((cat) => {
        const rows = items.filter((i) => i.catId === cat.id).sort((a, b) => a.order - b.order);
        const catDone = rows.filter((i) => wtOf(i).pct >= 100).length;
        return (
          <div key={cat.id}>
            <div className="sowwt-cathead">
              <span>{sectLabel(cat.section)} · {cat.name}</span>
              <span>{catDone}/{rows.length} done</span>
            </div>
            {rows.map((it) => {
              const wt = wtOf(it);
              const done = wt.pct >= 100;
              const wip = !done && wt.pct > 0;
              const mats = sowMatsBackfill(sowMatsOf(it.raw), materials);
              const thumb = mats.find((m) => m.img)?.img;
              return (
                <div key={it.id} className="sowwt-row">
                  <button
                    className={"sowwt-st" + (done ? " done" : wip ? " wip" : "")}
                    title="Tap to cycle: not started → in progress → done"
                    onClick={() => cycle(it)}
                  >
                    {done ? "✓ Done" : wip ? wt.pct + "%" : "Not started"}
                  </button>
                  {thumb ? <img className="sowwt-mat" src={thumb} alt="" referrerPolicy="no-referrer" onError={hideImg} /> : null}
                  <span className={"sowwt-name" + (done ? " done" : "")}>
                    <span className="sowwt-title">
                      {it.name}
                      <span className="sowwt-qty"> · {it.qty || 1} {it.unit || "each"}</span>
                      {done && wt.at ? <span className="sub"> · checked {wt.at}</span> : null}
                    </span>
                    {it.desc ? <span className="sowwt-desc">{it.desc}</span> : null}
                    {mats.length ? (
                      <span className="sowwt-mats">
                        {mats.map((m, i) => (
                          <span key={i} className="sowwt-matchip">
                            {m.url
                              ? <a href={m.url} target="_blank" rel="noreferrer noopener">{m.name}</a>
                              : m.name}
                            {m.qty > 1 ? <span className="sub"> ×{m.qty}</span> : null}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </span>
                  <span className="sowwt-pctwrap" title="Percent of this item complete">
                    <input
                      key={it.id + ":" + wt.pct}
                      className="moneyinput sowwt-pct"
                      inputMode="numeric"
                      defaultValue={String(wt.pct)}
                      onBlur={(e) => setPct(it, e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                    />
                    <span className="sowwt-pctsfx">% done</span>
                  </span>
                  <span className="sowwt-amt">{usd(line(it))}</span>
                  <button className="btn xs" title="Add check-off photos (as many as you want)" onClick={() => askPhoto(it)}>
                    📷{(wt.photos ?? []).length ? <span className="sowwt-n">{(wt.photos ?? []).length}</span> : null}
                  </button>
                  {uploading[it.id] ? <span className="sowwt-up" title="Uploading…">⬆ {uploading[it.id]}</span> : null}
                  <button
                    className={"btn xs" + (wt.note ? " danger" : "")}
                    title={wt.note ? "Edit the flagged issue" : "Flag an issue on this item"}
                    onClick={() => { setNoteFor(noteFor === it.id ? null : it.id); setNoteText(wt.note ?? ""); }}
                  >⚑</button>
                  {(wt.photos ?? []).length ? (
                    <span className="sowwt-thumbs">
                      {(wt.photos ?? []).slice(0, 8).map((p, i) => (
                        <img key={i} className="sowwt-thumb" src={p} alt="" loading="lazy" title="Tap to view" onError={hideImg} onClick={() => setViewer({ itemId: it.id, idx: i })} />
                      ))}
                      {(wt.photos ?? []).length > 8 ? (
                        <button className="sowwt-more" title="See all photos" onClick={() => setViewer({ itemId: it.id, idx: 8 })}>+{(wt.photos ?? []).length - 8}</button>
                      ) : null}
                    </span>
                  ) : null}
                  {noteFor === it.id ? (
                    <input
                      className="moneyinput sowwt-note"
                      autoFocus
                      placeholder="What's the issue? (empty clears the flag)"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onBlur={() => saveNote(it)}
                      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                    />
                  ) : wt.note ? (
                    <span className="sowwt-noteview">⚑ {wt.note}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** v1 scopeWoodwork — the dedicated Wood Work calculator. Renders the woodwork
 * section's combined-price scope items as simple rows (name · qty · price each ·
 * line total · delete). The v2 twist: these are real scope items persisted
 * through the door (v1's window._WW was ephemeral). */
function WoodworkPanel({
  items,
  onAdd,
  onPatch,
  onDelete,
}: {
  items: SowItem[];
  onAdd: () => void;
  onPatch: (it: SowItem, patch: Partial<SowItem>) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="sowww">
      <div className="sowwwhead">
        <div className="sowwwheadl">
          <span className="sowwwheadic">▤</span>
          <strong>Wood Work</strong>
        </div>
        <button className="btn" onClick={onAdd}>＋ Add Wood Work Item</button>
      </div>
      {items.length ? (
        <div className="sowwwlist">
          {items.map((it) => (
            <WoodworkRow key={it.id} it={it} onPatch={(patch) => onPatch(it, patch)} onDelete={() => onDelete(it.id)} />
          ))}
        </div>
      ) : (
        <div className="sowwwempty">No wood work items yet.</div>
      )}
    </div>
  );
}

/** One wood-work row: name + inline Qty / Price each (commit on blur, matching
 * the normal item rows) + line total + delete. Price each is the item's
 * `combined` (useCombined items). */
function WoodworkRow({
  it,
  onPatch,
  onDelete,
}: {
  it: SowItem;
  onPatch: (patch: Partial<SowItem>) => void;
  onDelete: () => void;
}) {
  const [qty, setQty] = useState(String(it.qty));
  const [price, setPrice] = useState(String(it.combined));
  // resync when the item changes underneath us (add, delete-shift, reload).
  useEffect(() => { setQty(String(it.qty)); }, [it.qty]);
  useEffect(() => { setPrice(String(it.combined)); }, [it.combined]);
  const total = (it.qty || 0) * (it.combined || 0);
  return (
    <div className="sowwwrow">
      <span className="sowwwname">{it.name}</span>
      <label className="sowwwfld">
        <span>Qty</span>
        <input
          className="sowwwqty"
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => { const v = numOf(qty); if (v !== it.qty) onPatch({ qty: v }); }}
        />
      </label>
      <label className="sowwwfld">
        <span>Price each $</span>
        <input
          className="sowwwprice"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={() => { const v = numOf(price); if (v !== it.combined) onPatch({ combined: v }); }}
        />
      </label>
      <span className="sowwwtot">{usd(total)}</span>
      <button className="sowwwdel" title="Delete" onClick={onDelete}>✕</button>
    </div>
  );
}

/** v1 scopeWWAdd modal (mirrors AddCatModal): Item Name + Qty + Price each. */
function AddWoodworkModal({ onCancel, onAdd }: {
  onCancel: () => void;
  onAdd: (name: string, qty: number, price: number) => void;
}) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("0");
  const submit = () => { if (name.trim()) onAdd(name, numOf(qty), numOf(price)); };
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Add Wood Work Item</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <label className="lbl">Item Name</label>
          <input
            className="moneyinput"
            autoFocus
            placeholder="e.g. Shaker cabinets"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <div className="sowfldrow" style={{ marginTop: 10 }}>
            <label className="sowfld">
              <span>Qty</span>
              <input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
            </label>
            <label className="sowfld">
              <span>Price each $</span>
              <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
          </div>
          <div className="cmpmodalacts" style={{ marginTop: 14 }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn primary" onClick={submit} disabled={!name.trim()}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** v1 scopeItemRow — a flex row (not a table cell): material thumbnails,
 * name + linked-material pills, description, photo badge, inline qty/unit and
 * labor/material (or combined) money inputs, row total, and row actions. Number
 * inputs keep local string state and commit on blur (v1 uses `onchange`), so
 * typing never queues an op per keystroke. */
function ItemRow({
  it,
  meta,
  materials,
  onPatch,
  onEdit,
  onCopy,
  onDelete,
  onDragStart,
  onDrop,
}: {
  it: SowItem;
  meta: SowMeta;
  materials: import("../materials/api").CatalogMaterial[];
  onPatch: (patch: Partial<SowItem>) => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDrop: () => void;
}) {
  const [qty, setQty] = useState(String(it.qty));
  const [unit, setUnit] = useState(it.unit);
  const [labor, setLabor] = useState(String(it.labor));
  const [material, setMaterial] = useState(String(it.material));
  const [combined, setCombined] = useState(String(it.combined));
  // resync when the item changes underneath us (paste, reorder, modal save).
  useEffect(() => { setQty(String(it.qty)); }, [it.qty]);
  useEffect(() => { setUnit(it.unit); }, [it.unit]);
  useEffect(() => { setLabor(String(it.labor)); }, [it.labor]);
  useEffect(() => { setMaterial(String(it.material)); }, [it.material]);
  useEffect(() => { setCombined(String(it.combined)); }, [it.combined]);

  // Read legacy single-material fields too, then backfill the thumbnail/link
  // from the catalog by name (v1 scopeMatFill) so v1-linked materials show.
  // memoized: stable across unrelated re-renders, so the pill/thumbnail React
  // keys (mat ids) never churn and the <img> elements are never remounted.
  const mats = useMemo(() => sowMatsBackfill(sowMatsOf(it.raw), materials), [it.raw, materials]);
  const thumbs = mats.filter((m) => m.img).slice(0, 3);
  const mk = it.useCombined ? 0 : itemMarkup(it, meta);
  const q = it.qty || 0;
  const lT = q * it.labor;
  const mT = q * it.material * (1 + mk / 100);
  const total = it.useCombined ? q * it.combined : lT + mT;

  const commit = (patch: Partial<SowItem>) => onPatch(patch);
  const isPhone = usePhone();

  /* Phone (Tom 2026-07-30): the row was five lines of inputs plus four ~24px
     icons, two of which (🖼 and ✎) were the same door. Now two lines; the
     whole row IS the door and opens the item editor, which holds qty, unit,
     prices, photos — and Duplicate / Delete as proper rows. Inline commit-on-
     blur inputs and drag grips are desktop tools. */
  if (isPhone) {
    const firstMat = mats[0];
    const matLbl = firstMat ? (firstMat.name.length > 26 ? firstMat.name.slice(0, 25) + "…" : firstMat.name) : null;
    return (
      <button type="button" className="sowirow phone" onClick={onEdit}>
        <span className="sowipl">
          <span className="sowiplname">
            {it.name}
            {it.photos.length ? <span className="sowiphn">🖼 {it.photos.length}</span> : null}
          </span>
          <span className="sowiplmeta">
            {q} {it.unit}
            {matLbl ? <> · {matLbl}{mats.length > 1 ? ` +${mats.length - 1}` : ""}</> : null}
          </span>
        </span>
        <span className="sowiplr">{usd(total)} <span className="sowiplchev">›</span></span>
      </button>
    );
  }

  return (
    <div className="sowirow" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); onDrop(); }}>
      <span
        className="sowgrip"
        draggable
        onDragStart={(e) => { e.stopPropagation(); onDragStart(); }}
        title="Drag to reorder"
      >
        ⠿
      </span>
      {thumbs.map((m) =>
        m.url ? (
          <a
            key={m.id}
            className="sowithumb"
            href={m.url}
            target="_blank"
            rel="noreferrer noopener"
            draggable={false}
            title="Open material link"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={m.img} referrerPolicy="no-referrer" loading="lazy" alt="" onError={hideThumb} />
          </a>
        ) : (
          <span key={m.id} className="sowithumb">
            <img src={m.img} referrerPolicy="no-referrer" loading="lazy" alt="" onError={hideThumb} />
          </span>
        ),
      )}
      <div className="sowiname">
        <div className="sowinametop">
          <span className="sowinamet">{it.name}</span>
          {mats.map((m) => {
            const short = m.name.length > 40 ? m.name.slice(0, 39) + "…" : m.name;
            const qtyLbl = (m.qty || 1) > 1 ? ` ×${m.qty}` : "";
            return m.url ? (
              <a
                key={m.id}
                className="sowmatpill link"
                href={m.url}
                target="_blank"
                rel="noreferrer noopener"
                title={"Open material link — " + m.name}
                onClick={(e) => e.stopPropagation()}
              >
                {short}{qtyLbl}
              </a>
            ) : (
              <span key={m.id} className="sowmatpill" title={m.name}>{short}{qtyLbl}</span>
            );
          })}
        </div>
        {it.desc ? <div className="sowidesc">{it.desc}</div> : null}
        {it.photos.length ? <div className="sowiphoto">🖼 {it.photos.length}</div> : null}
      </div>
      <div className="sowifld">
        <span className="sowilbl">Qty</span>
        <input
          className="sowiqty"
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => { const v = numOf(qty); if (v !== it.qty) commit({ qty: v }); }}
        />
        <select
          className="sowiunit"
          value={unit}
          onChange={(e) => { setUnit(e.target.value); commit({ unit: e.target.value }); }}
        >
          {SOW_UNITS.map((u) => (
            <option key={u}>{u}</option>
          ))}
        </select>
      </div>
      {it.useCombined ? (
        <div className="sowifld" title="Combined $ per unit">
          <span className="sowilbl">Combined $</span>
          <input
            className="sowicomb"
            type="number"
            value={combined}
            onChange={(e) => setCombined(e.target.value)}
            onBlur={() => { const v = numOf(combined); if (v !== it.combined) commit({ combined: v }); }}
          />
        </div>
      ) : (
        <>
          <div className="sowifld" title="Labor $ per unit">
            <span className="sowilbl">Labor $</span>
            <input
              className="sowimoney"
              type="number"
              value={labor}
              onChange={(e) => setLabor(e.target.value)}
              onBlur={() => { const v = numOf(labor); if (v !== it.labor) commit({ labor: v }); }}
            />
          </div>
          <div className="sowifld" title="Material $ per unit (cost, before markup)">
            <span className="sowilbl">Mat $</span>
            <input
              className="sowimoney"
              type="number"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              onBlur={() => { const v = numOf(material); if (v !== it.material) commit({ material: v }); }}
            />
            {mk > 0 ? <span className="sowimk" title={`${mk}% material markup is added to the total`}>+{mk}%</span> : null}
          </div>
        </>
      )}
      <div className="sowitot">
        <div className="sowitotb">{usd(total)}</div>
        {it.useCombined ? null : <div className="sowitotsub">Labor {usd(lT)} · Mat {usd(mT)}</div>}
      </div>
      <div className="sowiacts">
        <button className="sowiact" title="Photos" onClick={onEdit}>🖼</button>
        <button className="sowiact" title="Edit" onClick={onEdit}>✎</button>
        <button className="sowiact" title="Copy" onClick={onCopy}>⧉</button>
        <button className="sowiact del" title="Delete" onClick={onDelete}>×</button>
      </div>
    </div>
  );
}

/** Reset Scope is the most destructive click in the app — it wipes EVERY
 * category and item on the deal. So it confirms TWICE (Tom: "super important
 * this works 100%"):
 *   Step 1 — the typed gate: the exact blast radius (N categories, M items,
 *   $ total) is spelled out and the user must type "delete". Enter here only
 *   ADVANCES to step 2 — it can never fire the reset.
 *   Step 2 — a separate final screen. Its red button stays DISABLED for 2
 *   seconds (arming delay), so a double-Enter or double-click can never blow
 *   through both gates; Cancel is the focused default. Only an explicit press
 *   of the armed red button calls onReset.
 * Exported for the behavioral tests in tests/sow-reset-confirm.test.tsx. */
export function ResetScopeConfirm({
  cats,
  items,
  total,
  onCancel,
  onReset,
}: {
  cats: number;
  items: number;
  total: number;
  onCancel: () => void;
  onReset: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [step, setStep] = useState<1 | 2>(1);
  const [armed, setArmed] = useState(false);
  const unlocked = deleteUnlocked(typed);
  useEffect(() => {
    if (step !== 2) return;
    const t = window.setTimeout(() => setArmed(true), 2000);
    return () => window.clearTimeout(t);
  }, [step]);
  return (
    <div className="calmodalwrap" onClick={onCancel}>
      <div className="calmodal" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
        <div className="calmodalhead">
          <h2 className="deltitle">{step === 1 ? "Reset entire scope?" : "Final confirmation"}</h2>
          <button className="taskdel" onClick={onCancel}>×</button>
        </div>
        <div className="calmodalbody">
          {step === 1 ? (
            <>
              <p className="delmsg">
                This deletes <b>{cats} categor{cats === 1 ? "y" : "ies"}</b> and <b>{items} item{items === 1 ? "" : "s"}</b>
                {total > 0 ? <> ({usd(total)} of scoped work)</> : null} from this deal. You will be asked to confirm twice.
              </p>
              <div className="arvfield">
                <label className="lbl">
                  Type <b className="delword">delete</b> to continue
                </label>
                <input
                  className="moneyinput"
                  autoFocus
                  autoComplete="off"
                  placeholder="delete"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && unlocked) setStep(2); }}
                />
              </div>
            </>
          ) : (
            <p className="delmsg">
              ⚠ Last check — <b>{cats} categor{cats === 1 ? "y" : "ies"}</b> and <b>{items} item{items === 1 ? "" : "s"}</b> will
              be removed from this deal&apos;s scope. There is no undo button in the app.
            </p>
          )}
        </div>
        <div className="calmodalfoot">
          <button className="out" {...(step === 2 ? { autoFocus: true } : {})} onClick={onCancel}>
            {step === 1 ? "Cancel" : "Keep my scope"}
          </button>
          {step === 1 ? (
            <button className="btn danger" disabled={!unlocked} onClick={() => setStep(2)}>Continue…</button>
          ) : (
            <button className="btn danger" disabled={!armed} onClick={onReset}>
              {armed ? "Yes — reset everything" : "Hold on…"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** One editable sqft figure in the SOW header (Living / Gross area). Always
 * visible — an unknown value shows as an editable "—" so Tom can type it right
 * here (saved to the deal's pp_sqft / pp_grossSqft). Read-only without edit. */
function SqftCell({ label, value, onCommit }: { label: string; value: number | null; onCommit: ((v: number | null) => void) | null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const start = () => { if (!onCommit) return; setDraft(value ? String(value) : ""); setEditing(true); };
  const commit = () => {
    setEditing(false);
    if (!onCommit) return;
    const n = parseFloat(draft.replace(/[^0-9.]/g, ""));
    const v = Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    if (v !== (value ?? null)) onCommit(v);
  };
  return (
    <span className="sowsqftitem">
      {label}{" "}
      {editing ? (
        <input
          className="sowsqftin"
          autoFocus
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        />
      ) : (
        <button type="button" className={"sowsqftval" + (onCommit ? " editable" : "")} title={onCommit ? "Click to edit" : undefined} onClick={start}>
          <b>{value ? value.toLocaleString() : "—"}</b>
        </button>
      )}
      {" "}sqft
    </span>
  );
}

/** v1 scopeRenameCat — a tiny modal (mirrors AddCatModal), no browser prompt. */
function RenameCatModal({ cat, onCancel, onSave }: { cat: SowCat; onCancel: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState(cat.name);
  const submit = () => { if (name.trim()) onSave(name); };
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Rename category</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <label className="lbl">Category Name</label>
          <input
            className="moneyinput"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <div className="cmpmodalacts" style={{ marginTop: 14 }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn primary" onClick={submit} disabled={!name.trim()}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** v1 scopeFlag modal — optional note; the flag itself + red styling. Saving
 * also creates a High-priority deal task (the parent handles it via onFlagTask). */
function FlagCatModal({ cat, onCancel, onSave }: { cat: SowCat; onCancel: () => void; onSave: (note: string) => void }) {
  const [note, setNote] = useState(cat.note ?? "");
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Flag for follow-up</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <p className="sub">Marks “{cat.name}” red, lists it in the follow-up banner, and adds a High-priority task.</p>
          <label className="lbl">Note (optional)</label>
          <textarea className="moneyinput" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs attention?" />
          <div className="cmpmodalacts" style={{ marginTop: 14 }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn danger" onClick={() => onSave(note)}>Flag</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ItemModal({
  initial,
  materials,
  onNeedMaterials,
  onPutMaterial,
  materialCats = [],
  onImportUrl,
  onCancel,
  onSave,
  onDuplicate,
  onDelete,
}: {
  initial: SowItem | null;
  materials: import("../materials/api").CatalogMaterial[];
  onNeedMaterials: () => void;
  onPutMaterial?: PutMaterialFn;
  materialCats?: MaterialCat[];
  onImportUrl?: (url: string, onWake: () => void) => Promise<MaterialPriceResult>;
  onCancel: () => void;
  onSave: (draft: SowItem) => void;
  /** Phone only: the item row's ⧉ / × moved in here — the row's whole job on
   *  a phone is to open this editor, so the editor must hold ALL the doors. */
  onDuplicate?: () => void;
  onDelete?: () => void;
}) {
  const modalPhone = usePhone();
  const [name, setName] = useState(initial?.name ?? "");
  const [qty, setQty] = useState(String(initial?.qty ?? 1));
  const [unit, setUnit] = useState(initial?.unit ?? "each");
  const [labor, setLabor] = useState(String(initial?.labor ?? ""));
  const [material, setMaterial] = useState(String(initial?.material ?? ""));
  const [useCombined, setUseCombined] = useState(initial?.useCombined ?? false);
  const [combined, setCombined] = useState(String(initial?.combined ?? ""));
  const [desc, setDesc] = useState(initial?.desc ?? "");
  const [laborNotes, setLaborNotes] = useState(initial?.laborNotes ?? "");
  const [materialNotes, setMaterialNotes] = useState(initial?.materialNotes ?? "");
  const [photos, setPhotos] = useState<string[]>(initial?.photos ?? []);
  const [mats, setMats] = useState<SowMatLink[]>(() => sowMatsBackfill(sowMatsOf(initial?.raw), materials));
  // Tom 2026-08-20: "even if you change manually and save it doesnt actually
  // save it goes back to the wrong price" — save() used to DISCARD a typed
  // Material $ whenever a material was linked. Typing in the field now sets
  // this override and the typed number wins; touching the linked materials
  // (add/qty/price/remove) re-syncs to their sum and clears it.
  const [matOverride, setMatOverride] = useState(false);
  // per-row price drafts so "8." doesn't snap to 8 mid-keystroke
  const [matPriceDraft, setMatPriceDraft] = useState<Record<string, string>>({});
  // catalog browser (v1 _scopeCat): open toggle, search, filters, sort.
  const [pickOpen, setPickOpen] = useState(false);
  // "new material" — opens the SAME full editor the Materials page uses
  // (URL import, SKU, vendor, notes…), so inline creation lacks nothing.
  const [nmOpen, setNmOpen] = useState(false);
  const [matQ, setMatQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [fCat, setFCat] = useState("");
  const [fSup, setFSup] = useState("");
  const [fMin, setFMin] = useState("");
  const [fMax, setFMax] = useState("");
  const [sort, setSort] = useState<"Relevance" | "Price ↑" | "Price ↓" | "Name">("Relevance");
  const descRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const n = (v: string): number => {
    const f = parseFloat(v.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(f) ? f : 0;
  };

  // the catalog is lazy-loaded (only when Materials page is opened); pull it now
  useEffect(() => {
    if (materials.length === 0) onNeedMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v1 autoGrowTA: the Description box grows to fit its text (no inner scrollbar).
  useEffect(() => {
    const el = descRef.current;
    if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
  }, [desc]);

  // v1 _scopeMatRecalc: the per-unit Material $ tracks the sum of linked mats.
  const setMatsAndPrice = (next: SowMatLink[]) => {
    setMats(next);
    setMatOverride(false);
    if (next.length) setMaterial(String(sowMatsSum(next)));
  };
  // price edit on a linked-material row — the sum (and Material $) follow live
  const setMatPrice = (id: string, v: string) => {
    setMatPriceDraft((p) => ({ ...p, [id]: v }));
    const num = parseFloat(v.replace(/[^0-9.]/g, ""));
    setMatsAndPrice(mats.map((x) => (x.id === id ? { ...x, price: Number.isFinite(num) ? num : 0 } : x)));
  };
  // "if you fix the original that should work" (Tom 2026-08-20): a corrected
  // price writes back to the CATALOG material (matched by name, same op the
  // Materials page uses, history appended) so every future link — and the
  // daily price sweep — starts from the right number, not the bad scrape.
  const pushCatalogPrice = (matName: string, price: number) => {
    if (!onPutMaterial) return;
    const cm = materials.find((m) => m.name === matName);
    if (!cm || cm.price === price) return;
    onPutMaterial(cm.natKey, {
      name: cm.name, categoryId: cm.categoryId, price,
      unit: cm.unit ?? "each", supplier: cm.supplier ?? "", vendor: cm.vendor ?? "",
      sku: cm.sku ?? "", internet: cm.internet ?? "", notes: cm.notes ?? "",
      website: cm.website ?? "", thumbnail: cm.thumbnail ?? "",
    }, [...cm.history, { date: new Date().toISOString().slice(0, 10), price }]);
    onNeedMaterials();
  };
  const commitMatPrice = (id: string) => {
    setMatPriceDraft((p) => { const q = { ...p }; delete q[id]; return q; });
    const link = mats.find((x) => x.id === id);
    if (link) pushCatalogPrice(link.name, link.price);
  };
  // Tom 2026-08-20: "if i change manually also it should change the price
  // also" — typing a Material $ while exactly ONE qty-1 material is linked
  // means "THAT material really costs this": the link and the catalog follow
  // the typed number. With several links (or qty > 1) the split would be a
  // guess, so the typed value just wins for this item (matOverride).
  const syncManualMaterial = () => {
    if (mats.length !== 1) return;
    const link = mats[0];
    if (!link || (link.qty || 0) !== 1) return;
    const v = n(material);
    if (!Number.isFinite(v) || v <= 0 || v === link.price) return;
    setMats([{ ...link, price: v }]);
    setMatOverride(false);          // sum == typed again, nothing to override
    pushCatalogPrice(link.name, v);
  };
  // v1 scopeCatPick: already-linked → qty +1; new → push a link; Material $ = sum.
  const addMat = (m: import("../materials/api").CatalogMaterial) => {
    const ex = mats.find((x) => x.name === m.name);
    if (ex) {
      setMatsAndPrice(mats.map((x) => (x === ex ? { ...x, qty: (x.qty || 0) + 1 } : x)));
    } else {
      const link: SowMatLink = {
        id: sowId("sm"), name: m.name, price: m.price ?? 0, qty: 1,
        ...(m.website ? { url: m.website } : {}),
        ...(m.thumbnail ? { img: m.thumbnail } : {}),
      };
      setMatsAndPrice([...mats, link]);
      // NO auto-naming (Tom 2026-08-20: "it puts the material name at the
      // item name. do not do this") — the item name is the TASK ("Install
      // door stoppers"), the material already shows on its linked row.
    }
  };
  const setMatQty = (id: string, v: string) => setMatsAndPrice(mats.map((x) => (x.id === id ? { ...x, qty: n(v) } : x)));
  const delMat = (id: string) => setMatsAndPrice(mats.filter((x) => x.id !== id));
  // The full-editor save: persist to the shared catalog (same op as the
  // Materials page), link it to this item, refresh the catalog list.
  const createMat = (input: MaterialInput) => {
    if (!onPutMaterial) return;
    onPutMaterial(matId(), input, []);
    setMatsAndPrice([...mats, {
      id: sowId("sm"), name: input.name, price: input.price || 0, qty: 1,
      ...(input.website ? { url: input.website } : {}),
      ...(input.thumbnail ? { img: input.thumbnail } : {}),
    }]);
    // no auto-naming — see addMat (Tom 2026-08-20)
    onNeedMaterials();
    setNmOpen(false);
  };

  // v1 scopeItemAddPhoto/DelPhoto. Photos ride inside the scope_items row, which
  // the write door caps at 16 KB — so downscale each to a small reference
  // thumbnail (≤360px JPEG) that actually persists, instead of a multi-MB
  // base64 blob that would be dropped on save.
  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        const src = reader.result;
        if (typeof src !== "string") return;
        const img = new Image();
        img.onload = () => {
          const MAX = 360;
          const scale = Math.min(1, MAX / Math.max(img.width || 1, img.height || 1));
          const w = Math.max(1, Math.round((img.width || 1) * scale));
          const h = Math.max(1, Math.round((img.height || 1) * scale));
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { setPhotos((p) => [...p, src]); return; }
          ctx.drawImage(img, 0, 0, w, h);
          let out = canvas.toDataURL("image/jpeg", 0.55);
          if (out.length > 11000) out = canvas.toDataURL("image/jpeg", 0.4);
          setPhotos((p) => [...p, out]);
        };
        img.onerror = () => setPhotos((p) => [...p, src]);
        img.src = src;
      };
      reader.readAsDataURL(f);
    });
  };
  const delPhoto = (idx: number) => setPhotos((p) => p.filter((_, i) => i !== idx));

  // distinct filter options + the filtered/sorted/capped catalog (v1 scopeCatFilter).
  const matCats = useMemo(() => Array.from(new Set(materials.map((m) => m.categoryId).filter((c): c is string => !!c))), [materials]);
  const matSups = useMemo(() => Array.from(new Set(materials.map((m) => m.supplier).filter((s): s is string => !!s))), [materials]);
  const catalog = useMemo(() => {
    const query = matQ.trim().toLowerCase();
    const mnN = parseFloat(fMin);
    const mxN = parseFloat(fMax);
    const mn = fMin && Number.isFinite(mnN) ? mnN : 0;
    const mx = fMax && Number.isFinite(mxN) ? mxN : Infinity;
    let r = materials.filter((m) => {
      if (query && ![m.name, m.supplier, m.sku, m.categoryId].some((f) => (f ?? "").toLowerCase().includes(query))) return false;
      if (fCat && m.categoryId !== fCat) return false;
      if (fSup && m.supplier !== fSup) return false;
      const p = m.price ?? 0;
      if (p < mn || p > mx) return false;
      return true;
    });
    if (sort === "Price ↑") r = r.slice().sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    else if (sort === "Price ↓") r = r.slice().sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    else if (sort === "Name") r = r.slice().sort((a, b) => a.name.localeCompare(b.name));
    return r.slice(0, 60); // filters/sort applied BEFORE the cap
  }, [materials, matQ, fCat, fSup, fMin, fMax, sort]);

  return (
    /* NO scrim-click dismiss on this one (Tom 2026-08-07: "i want it not to
       close it unless i click the X"). The item editor is the fullest form in
       the app — name, description, prices, materials, photos — and a stray
       click past its edge was throwing all of it away. Same input-loss class
       the audit's Escape-on-notes fix covered. ✕ and Cancel still close. */
    <div className="cmpmodalscrim">
      <div className="cmpmodal sowmodal sowitemmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>{initial ? "Edit item" : "Add item"}</h3>
            <button className="close" onClick={onCancel}>×</button>
          </div>
          <label className="sowfld">
            <span>Item name *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Interior paint — walls" />
          </label>
          <label className="sowfld">
            <span>Description</span>
            <textarea ref={descRef} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} style={{ resize: "none", overflow: "hidden" }} placeholder="What exactly gets done — colors, finishes, scope…" />
          </label>
          <div className="sowfldrow">
            <label className="sowfld">
              <span>Qty</span>
              <input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
            </label>
            <label className="sowfld">
              <span>Unit</span>
              <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                {SOW_UNITS.map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="sowchk">
            <input type="checkbox" checked={useCombined} onChange={(e) => setUseCombined(e.target.checked)} />
            <span>Combined price (labor + material as one number)</span>
          </label>
          {useCombined ? (
            <label className="sowfld">
              <span>Combined $ / unit</span>
              <input inputMode="decimal" value={combined} onChange={(e) => setCombined(e.target.value)} />
            </label>
          ) : (
            <div className="sowfldrow">
              <label className="sowfld">
                <span>Labor $ / unit</span>
                <input inputMode="decimal" value={labor} onChange={(e) => setLabor(e.target.value)} />
              </label>
              <label className="sowfld">
                <span>Material $ / unit</span>
                <input inputMode="decimal" value={material} onChange={(e) => { setMaterial(e.target.value); setMatOverride(true); }} onBlur={syncManualMaterial} />
              </label>
            </div>
          )}
          <div className="sowfldrow">
            <label className="sowfld">
              <span>Labor Notes</span>
              <input value={laborNotes} onChange={(e) => setLaborNotes(e.target.value)} />
            </label>
            <label className="sowfld">
              <span>Material Notes</span>
              <input value={materialNotes} onChange={(e) => setMaterialNotes(e.target.value)} />
            </label>
          </div>
          <div className="sowmatbox">
            <div className="sowmatboxhead">
              <span>Materials Catalog {mats.length ? <em className="sub">· sets Material $ = {usd(sowMatsSum(mats))}</em> : null}</span>
              <span className="sowmatboxacts">
                {onPutMaterial ? <button className="btn xs" onClick={() => { setNmOpen(true); setPickOpen(false); }}>＋ New material</button> : null}
                <button className="btn xs" onClick={() => { setPickOpen((v) => !v); setNmOpen(false); }}>{pickOpen ? "Close catalog" : "＋ Choose Material"}</button>
              </span>
            </div>
            {nmOpen && onPutMaterial ? (
              // The SAME full editor the Materials sidebar uses — product-link
              // import (Amazon/HD/Lowe's), price, SKU, vendor, notes, the works.
              <MaterialEditor
                init={{ id: "", input: emptyMaterial(materialCats[0]?.id ?? null), history: [] }}
                cats={materialCats}
                onCancel={() => setNmOpen(false)}
                onSave={createMat}
                {...(onImportUrl ? { onImportUrl } : {})}
              />
            ) : null}
            {mats.length ? (
              <div className="sowmatlinks">
                {mats.map((m) => (
                  <div key={m.id} className="sowmatlink">
                    {m.img ? <img src={m.img} referrerPolicy="no-referrer" alt="" onError={hideImg} /> : null}
                    <span className="sowmatname">{m.url ? <a href={m.url} target="_blank" rel="noreferrer noopener">{m.name}</a> : m.name}</span>
                    <input className="sowmatqty" inputMode="decimal" value={String(m.qty)} onChange={(e) => setMatQty(m.id, e.target.value)} title="Qty" />
                    <span className="sowmatx">×&nbsp;$</span>
                    <input
                      className="sowmatqty sowmatprice"
                      inputMode="decimal"
                      value={matPriceDraft[m.id] ?? String(m.price)}
                      onChange={(e) => setMatPrice(m.id, e.target.value)}
                      onBlur={() => commitMatPrice(m.id)}
                      title="Price — fixing a wrong import updates the catalog material too"
                    />
                    <span className="sowmatline">${((m.qty || 0) * (m.price || 0)).toFixed(2)}</span>
                    <button className="sowdel" title="Remove material" onClick={() => delMat(m.id)}>×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sownote">No materials selected. Enter Material $ manually or browse the catalog.</p>
            )}
            {pickOpen ? (
              <div className="sowmatpick">
                <div className="sowmatpickbar">
                  <input className="sowmatsearch" placeholder="Search name, supplier, SKU, category…" value={matQ} onChange={(e) => setMatQ(e.target.value)} />
                  <button className={"sowmatfilterbtn" + (showFilters ? " on" : "")} title="Filters" onClick={() => setShowFilters((v) => !v)}>⚙</button>
                  <select className="sowmatsort" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
                    {["Relevance", "Price ↑", "Price ↓", "Name"].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                {showFilters ? (
                  <div className="sowmatfilters">
                    <label>
                      <span>Category</span>
                      <select value={fCat} onChange={(e) => setFCat(e.target.value)}>
                        <option value="">All Categories</option>
                        {matCats.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Supplier</span>
                      <select value={fSup} onChange={(e) => setFSup(e.target.value)}>
                        <option value="">All Suppliers</option>
                        {matSups.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Min $</span>
                      <input inputMode="decimal" placeholder="0" value={fMin} onChange={(e) => setFMin(e.target.value)} />
                    </label>
                    <label>
                      <span>Max $</span>
                      <input inputMode="decimal" placeholder="∞" value={fMax} onChange={(e) => setFMax(e.target.value)} />
                    </label>
                  </div>
                ) : null}
                <div className="sowmatcards">
                  {materials.length === 0 ? (
                    <div className="sownote">Loading the catalog…</div>
                  ) : catalog.length === 0 ? (
                    <div className="sownote">No materials match your filters.</div>
                  ) : (
                    catalog.map((m) => (
                      <button key={m.natKey} className="sowmatcard" onClick={() => addMat(m)} title="Add this material">
                        {m.thumbnail ? (
                          <img className="sowmatcardimg" src={m.thumbnail} referrerPolicy="no-referrer" alt="" onError={hideImg} />
                        ) : (
                          <span className="sowmatcardimg ph">🖼</span>
                        )}
                        <span className="sowmatcardmain">
                          <span className="sowmatcardname">{m.name}</span>
                          <span className="sowmatcardmeta">
                            {m.categoryId ? <span className="sowpill">{m.categoryId}</span> : null}
                            {m.supplier ? <span className="sowmatcardsup">{m.supplier}</span> : null}
                          </span>
                          <span className="sowmatcardprice">{m.price !== null ? "$" + m.price.toFixed(2) : "—"}<span className="per">/each</span></span>
                        </span>
                        <span className="sowmatcardlink" title="Opens product link after adding">↗</span>
                      </button>
                    ))
                  )}
                </div>
                <p className="sownote">Click a material to link it (sets Material $ to the sum of linked materials).</p>
              </div>
            ) : null}
          </div>
          <div className="sowphotobox">
            <div className="sowphotohead">
              <span>🖼 Item Photos</span>
              <button className="btn xs" onClick={() => fileRef.current?.click()}>＋ Add Photos</button>
            </div>
            <input ref={fileRef} className="sowhiddenfile" type="file" accept="image/*" multiple onChange={(e) => { addPhotos(e.target.files); e.currentTarget.value = ""; }} />
            {photos.length ? (
              <div className="sowphotogrid">
                {photos.map((p, idx) => (
                  <div key={idx} className="sowphoto">
                    <img src={p} alt="" onError={hideImg} />
                    <button className="sowphotodel" title="Remove photo" onClick={() => delPhoto(idx)}>×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sownote">No photos yet. Add reference pictures for this item.</p>
            )}
          </div>
          {modalPhone && (onDuplicate || onDelete) ? (
            <div className="sowmodalrows">
              {onDuplicate ? <button className="pdmitem" onClick={onDuplicate}>⧉ Duplicate item</button> : null}
              {onDelete ? <button className="pdmitem danger" onClick={onDelete}>× Delete item</button> : null}
            </div>
          ) : null}
          <div className="cmpmodalacts">
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button
              className="btn primary"
              disabled={!name.trim()}
              onClick={() => {
                // mats live in raw (round-trips to v1's item.mats); the item's
                // per-unit Material $ equals their sum when any are linked.
                const raw = { ...(initial?.raw ?? {}) };
                if (mats.length) raw["mats"] = mats.map((m) => ({ id: m.id, name: m.name, price: m.price, qty: m.qty, ...(m.url ? { url: m.url } : {}), ...(m.img ? { img: m.img } : {}) }));
                else delete raw["mats"];
                onSave({
                  id: initial?.id ?? "",
                  catId: initial?.catId ?? "",
                  name: name.trim(),
                  qty: n(qty),
                  unit,
                  labor: n(labor),
                  // typed override wins; linked-material sum only when the
                  // user hasn't touched the field (Tom 2026-08-20: manual
                  // price edits used to silently revert to the bad import)
                  material: mats.length && !matOverride ? sowMatsSum(mats) : n(material),
                  markupMode: initial?.markupMode ?? "inherit",
                  markupPct: initial?.markupPct ?? 0,
                  useCombined,
                  combined: n(combined),
                  desc: desc.trim(),
                  laborNotes: laborNotes.trim(),
                  materialNotes: materialNotes.trim(),
                  photos,
                  order: initial?.order ?? 0,
                  isSub: initial?.isSub ?? false,
                  raw,
                });
              }}
            >
              {initial ? "Save item" : "Add item"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Export Scope of Work — v1 scopeExport modal (format / prices / categories /
 * options / PDF header+footer). Client-side only. */
function ExportModal({
  sow,
  addr,
  materials = [],
  onClose,
}: {
  sow: SowState;
  addr: string;
  /** catalog — backfills material links in the export (name → website) */
  materials?: { name: string; thumbnail: string | null; website: string | null }[];
  onClose: () => void;
}) {
  const withItems = useMemo(
    () => sow.cats.filter((c) => sow.items.some((i) => i.catId === c.id)).sort((a, b) => a.order - b.order),
    [sow],
  );
  const allCats = useMemo(() => [...sow.cats].sort((a, b) => a.order - b.order), [sow]);
  const [format, setFormat] = useState<"csv" | "pdf">("csv");
  const [prices, setPrices] = useState<ExpPrices>("both");
  const [showEmpty, setShowEmpty] = useState(false);
  const [sel, setSel] = useState<Set<string>>(() => new Set(withItems.map((c) => c.id)));
  const [headers, setHeaders] = useState(true);
  const [subtotals, setSubtotals] = useState(true);
  const [grand, setGrand] = useState(true);
  const [withDesc, setWithDesc] = useState(true); // item descriptions + notes
  const [withMats, setWithMats] = useState(true); // linked materials + product links
  const [company, setCompany] = useState("");
  const [footer, setFooter] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const logoRef = useRef<HTMLInputElement | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // v1 scopeExpRender preview (L238): live stats for the CURRENT selection —
  // labor/material use catTotals (combined items sit outside them), grand total
  // is the true per-cat total (so it includes combined-price items).
  const stats = useMemo(() => {
    let cats = 0, items = 0, labor = 0, material = 0, total = 0;
    for (const c of allCats) {
      if (!sel.has(c.id)) continue;
      cats += 1;
      const t = catTotals(sow.items, c.id, sow.meta);
      labor += t.labor; material += t.material; total += t.total;
      items += sow.items.filter((i) => i.catId === c.id && !i.isSub).length;
    }
    return { cats, items, labor, material, total };
  }, [allCats, sel, sow]);

  const listCats = showEmpty ? allCats : withItems;
  const toggle = (id: string) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const opts = (): ExpOpts => ({ prices, headers, subtotals, grand, desc: withDesc, materials: withMats, catIds: allCats.filter((c) => sel.has(c.id)).map((c) => c.id), company: company.trim(), footer: footer.trim(), ...(logo ? { logo } : {}) });

  // v1 logo upload — downscale to a small (≤160px) data URL so the PDF header
  // image stays light (mirrors the item-photo downscale). PNG keeps edges crisp.
  const onLogoFile = (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") return;
      const img = new Image();
      img.onload = () => {
        const MAX = 160;
        const scale = Math.min(1, MAX / Math.max(img.width || 1, img.height || 1));
        const w = Math.max(1, Math.round((img.width || 1) * scale));
        const h = Math.max(1, Math.round((img.height || 1) * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setLogo(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        setLogo(canvas.toDataURL("image/png"));
      };
      img.onerror = () => setLogo(src);
      img.src = src;
    };
    reader.readAsDataURL(f);
  };

  const [busyPdf, setBusyPdf] = useState(false);
  const run = () => {
    if (!sel.size) { setNote("Select at least one category."); return; }
    if (format === "csv") {
      downloadCsv("scope-of-work.csv", buildScopeCsv(sow, opts(), materials));
      onClose();
    } else {
      // REAL .pdf download (no print dialog). Falls back to the printable tab
      // only if the on-demand PDF engine can't load (e.g. offline).
      setBusyPdf(true); setNote(null);
      void downloadScopePdf(sow, opts(), addr, materials)
        .then(() => setNote("Downloaded scope-of-work.pdf ✓"))
        .catch(() => {
          const how = openPrintable(buildScopePdfHtml(sow, opts(), addr, materials));
          setNote(how === "print" ? "PDF engine unavailable — opened the printable view; choose “Save as PDF”." : "PDF engine unavailable and popup blocked — downloaded the HTML instead.");
        })
        .finally(() => setBusyPdf(false));
    }
  };
  // the old print flow, kept as an explicit secondary action
  const printView = () => {
    if (!sel.size) { setNote("Select at least one category."); return; }
    const how = openPrintable(buildScopePdfHtml(sow, opts(), addr, materials));
    setNote(how === "print" ? "Opened the printable view." : "Popup blocked — downloaded the HTML; open it to print.");
  };

  const PRICE_OPTS: [ExpPrices, string][] = [["both", "Both"], ["material", "Material"], ["labor", "Labor"], ["none", "None"]];

  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal sowmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Export scope of work</h3>
            <button className="close" onClick={onClose}>×</button>
          </div>

          <div className="sowexprow">
            <span className="sowexplbl">Format</span>
            <div className="sowexpseg">
              {(["csv", "pdf"] as const).map((f) => (
                <button key={f} className={"sowexpsegbtn" + (format === f ? " on" : "")} onClick={() => setFormat(f)}>{f.toUpperCase()}</button>
              ))}
            </div>
          </div>

          <div className="sowexprow">
            <span className="sowexplbl">Prices</span>
            <div className="sowexpseg">
              {PRICE_OPTS.map(([v, l]) => (
                <button key={v} className={"sowexpsegbtn" + (prices === v ? " on" : "")} onClick={() => setPrices(v)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="sowexpcats">
            <div className="sowexpcatshead">
              <span className="sowexplbl">Categories</span>
              <span className="sowexpsellinks">
                <button onClick={() => setSel(new Set(listCats.map((c) => c.id)))}>All</button>
                <button onClick={() => setSel(new Set())}>None</button>
                <button onClick={() => setSel(new Set(withItems.map((c) => c.id)))}>Non-Empty</button>
                <label className="sowchk inline"><input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />empty</label>
              </span>
            </div>
            <div className="sowexpcatlist">
              {listCats.length ? listCats.map((c) => {
                const t = catTotals(sow.items, c.id, sow.meta);
                const n = sow.items.filter((i) => i.catId === c.id).length;
                return (
                  <label key={c.id} className={"sowexpcat" + (sel.has(c.id) ? " on" : "")}>
                    <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
                    <span className="sowexpcatname">{c.name}</span>
                    <span className="sowexpcatmeta">{n} item{n === 1 ? "" : "s"} · {usd(t.total)}</span>
                  </label>
                );
              }) : <div className="sownote">No categories.</div>}
            </div>
          </div>

          <div className="sowexpopts">
            <label className="sowchk"><input type="checkbox" checked={headers} onChange={(e) => setHeaders(e.target.checked)} /><span>Category headers</span></label>
            <label className={"sowchk" + (prices === "none" ? " off" : "")} title={prices === "none" ? "Subtotals are prices — pick a price mode to include them" : undefined}>
              <input type="checkbox" checked={subtotals} disabled={prices === "none"} onChange={(e) => setSubtotals(e.target.checked)} /><span>Category subtotals</span>
            </label>
            <label className={"sowchk" + (prices === "none" ? " off" : "")} title={prices === "none" ? "The grand total is a price — pick a price mode to include it" : undefined}>
              <input type="checkbox" checked={grand} disabled={prices === "none"} onChange={(e) => setGrand(e.target.checked)} /><span>Grand total</span>
            </label>
            <label className="sowchk"><input type="checkbox" checked={withDesc} onChange={(e) => setWithDesc(e.target.checked)} /><span>Item descriptions</span></label>
            <label className="sowchk"><input type="checkbox" checked={withMats} onChange={(e) => setWithMats(e.target.checked)} /><span>Materials + links</span></label>
          </div>

          {format === "pdf" ? (
            <>
              <label className="sowfld"><span>Company name (PDF header)</span><input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g. ETO Property Group" /></label>
              <div className="sowfld">
                <span>Company logo (PDF header, top-right)</span>
                <div className="sowlogorow">
                  <button className="sowlogobtn" onClick={() => logoRef.current?.click()}>⤒ Upload logo</button>
                  <input ref={logoRef} className="sowhiddenfile" type="file" accept="image/*" onChange={(e) => { onLogoFile(e.target.files); e.currentTarget.value = ""; }} />
                  {logo ? (
                    <span className="sowlogoprev">
                      <img src={logo} alt="" />
                      <button className="sowdel" title="Remove logo" onClick={() => setLogo(null)}>×</button>
                    </span>
                  ) : (
                    <span className="sowloghint">Optional · shown top-right of the header</span>
                  )}
                </div>
              </div>
              <label className="sowfld"><span>Footer notes / terms</span><textarea rows={2} value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="e.g. Prices are estimates, subject to change." /></label>
            </>
          ) : null}

          <div className="sowexpstats">
            <div className="sowexpstatshd">Export preview</div>
            <div className="sowexpstatsgrid">
              <div className="sowexpstat"><b>{stats.cats}</b><span>Categor{stats.cats === 1 ? "y" : "ies"}</span></div>
              <div className="sowexpstat"><b>{stats.items}</b><span>Item{stats.items === 1 ? "" : "s"}</span></div>
              <div className="sowexpstat"><b>{usd(stats.labor)}</b><span>Labor</span></div>
              <div className="sowexpstat"><b>{usd(stats.material)}</b><span>Material</span></div>
            </div>
            <div className="sowexpstatstot"><span>Grand total</span><b>{usd(stats.total)}</b></div>
          </div>

          {note ? <p className="sowexpnote">{note}</p> : null}

          <div className="cmpmodalacts">
            <button className="btn" onClick={onClose}>Close</button>
            {format === "pdf" ? <button className="btn" disabled={!sel.size} title="Open the print dialog instead of downloading" onClick={printView}>🖨 Print</button> : null}
            <button className="btn primary" disabled={!sel.size || busyPdf} onClick={run}>
              {format === "pdf" ? (busyPdf ? "Building PDF…" : "⤓ Download PDF") : "Export CSV"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** SOW templates — save the current scope as a reusable template, preview a
 * saved/shared one, and apply it in Add / Merge / Replace mode (v1
 * scopeApplyTemplate). Since Phase 28 the org SCOPE_TPLS blob is door-writable:
 * new saves go ORG-WIDE (v1's exact shape, so v1's modal keeps working), shared
 * templates get rename/delete, and older device-local templates carry a
 * one-click "Share to team". Without onSaveScopeTpls (door not migrated yet /
 * read-only role) everything falls back to the old local behavior. */
function TemplatesModal({
  sow,
  sharedTemplates,
  scopeTplsRaw,
  onSaveScopeTpls,
  onApply,
  onClose,
}: {
  sow: SowState;
  /** org-wide templates parsed from v1's SCOPE_TPLS */
  sharedTemplates: SowTemplate[];
  /** the raw v1-shaped SCOPE_TPLS array behind sharedTemplates */
  scopeTplsRaw: unknown[];
  onSaveScopeTpls?: (all: unknown[]) => void;
  onApply: (tpl: SowTemplate, mode: "add" | "merge" | "replace") => void;
  onClose: () => void;
}) {
  const [list, setList] = useState<SowTemplate[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(true);
  const [search, setSearch] = useState("");
  const [selId, setSelId] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState("");
  // Partial-scope save: which categories to include. null = ALL (whole scope).
  const [pickCats, setPickCats] = useState<Set<string> | null>(null);

  useEffect(() => {
    void loadTemplates().then((l) => { setList(l); setBusy(false); });
  }, []);

  const itemCount = useMemo(() => sow.items.filter((i) => !i.isSub).length, [sow]);
  // categories that actually carry items — the only ones worth saving/picking
  const saveCats = useMemo(() => {
    const withItems = new Set(sow.items.map((i) => i.catId));
    return [...sow.cats].sort((a, b) => a.order - b.order).filter((c) => withItems.has(c.id));
  }, [sow]);
  const catSelected = (id: string) => pickCats === null || pickCats.has(id);
  const toggleCat = (id: string) =>
    setPickCats((prev) => {
      const base = prev ?? new Set(saveCats.map((c) => c.id)); // null → start from all
      const next = new Set(base);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  // items covered by the current selection — gates the Save button + count copy
  const selItemCount = useMemo(
    () => sow.items.filter((i) => !i.isSub && catSelected(i.catId)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sow, pickCats],
  );
  const selected = useMemo(
    () => [...sharedTemplates, ...list].find((t) => t.id === selId) ?? null,
    [sharedTemplates, list, selId],
  );

  const tplItemCount = (t: SowTemplate) => t.cats.reduce((n, c) => n + c.items.length, 0);
  const secLabel = (k: string) => SOW_SECTIONS.find(([s]) => s === k)?.[1] ?? k;

  const save = async () => {
    const nm = name.trim();
    if (!nm || !selItemCount) return;
    // pass the selected category set only when it's a real subset (else whole scope)
    const subset = pickCats && pickCats.size < saveCats.length ? pickCats : undefined;
    const tpl = scopeToTemplate(nm, sow, Date.now(), subset);
    if (onSaveScopeTpls) {
      // org-wide (Phase 28): everyone on the team sees it, v1 included
      onSaveScopeTpls(scopeTplsAppend(scopeTplsRaw, templateToV1Entry(tpl)));
      setName("");
      setPickCats(null);
      return;
    }
    const next = [tpl, ...list.filter((t) => t.name !== nm)];
    setList(next);
    setName("");
    setPickCats(null);
    await saveTemplates(next);
  };
  const del = async (id: string) => {
    const next = list.filter((t) => t.id !== id);
    setList(next);
    if (selId === id) setSelId(null);
    await saveTemplates(next);
  };
  const delShared = (id: string) => {
    const idx = sharedTplIndex(id);
    if (idx < 0 || !onSaveScopeTpls) return;
    onSaveScopeTpls(scopeTplsDelete(scopeTplsRaw, idx));
    if (selId === id) setSelId(null);
    setConfirmDel(null);
  };
  const renameShared = (id: string) => {
    const idx = sharedTplIndex(id);
    const nm = renameVal.trim();
    if (idx < 0 || !nm || !onSaveScopeTpls) { setRenaming(null); return; }
    onSaveScopeTpls(scopeTplsRename(scopeTplsRaw, idx, nm));
    setRenaming(null);
  };
  /** Move a device-local template into the org blob (then drop the local copy). */
  const shareUp = async (t: SowTemplate) => {
    if (!onSaveScopeTpls) return;
    onSaveScopeTpls(scopeTplsAppend(scopeTplsRaw, templateToV1Entry(t)));
    await del(t.id);
  };

  // ---- item-level editor for a SHARED template (v1 scopeTplEdRender port) ----
  const [edId, setEdId] = useState<string | null>(null);
  const [edEntry, setEdEntry] = useState<Record<string, unknown> | null>(null);
  const edCats = (): Record<string, unknown>[] => {
    const p = edEntry?.["payload"];
    const payload = p !== null && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, unknown>) : {};
    return Array.isArray(payload["cats"]) ? (payload["cats"] as Record<string, unknown>[]) : [];
  };
  const edMut = (fn: (cats: Record<string, unknown>[]) => void) => {
    if (!edEntry) return;
    const next = JSON.parse(JSON.stringify(edEntry)) as Record<string, unknown>;
    const payload = next["payload"] as Record<string, unknown>;
    fn(payload["cats"] as Record<string, unknown>[]);
    setEdEntry(next);
  };
  const openEditor = (t: SowTemplate) => {
    const idx = sharedTplIndex(t.id);
    const copy = cloneTplEntry(scopeTplsRaw, idx);
    if (!copy) return;
    setEdId(t.id);
    setEdEntry(copy);
    setSelId(null);
  };
  const saveEditor = () => {
    if (!edId || !edEntry || !onSaveScopeTpls) return;
    const idx = sharedTplIndex(edId);
    onSaveScopeTpls(scopeTplsUpdateEntry(scopeTplsRaw, idx, edEntry));
    setEdId(null);
    setEdEntry(null);
  };

  // filter by template name OR any of its category names/sections (a light
  // type/section search over both lists).
  const q = search.trim().toLowerCase();
  const match = (t: SowTemplate) =>
    !q ||
    t.name.toLowerCase().includes(q) ||
    t.cats.some((c) => c.name.toLowerCase().includes(q) || secLabel(c.section).toLowerCase().includes(q));
  const shared = sharedTemplates.filter(match);
  const device = list.filter(match);

  const row = (t: SowTemplate, kind: "shared" | "device") => (
    <div key={t.id} className={"sowtplrow" + (selId === t.id ? " on" : "")}>
      {renaming === t.id ? (
        <input
          className="sowtplrename"
          value={renameVal}
          autoFocus
          onChange={(e) => setRenameVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") renameShared(t.id); if (e.key === "Escape") setRenaming(null); }}
          onBlur={() => renameShared(t.id)}
        />
      ) : (
        <span className="sowtplname">{t.name}</span>
      )}
      <span className="sowtplmeta">{t.cats.length} categor{t.cats.length === 1 ? "y" : "ies"} · {tplItemCount(t)} items</span>
      <button className="btn xs" onClick={() => setSelId(selId === t.id ? null : t.id)}>{selId === t.id ? "Hide" : "Preview"}</button>
      {kind === "shared" && onSaveScopeTpls ? (
        <>
          <button className="btn xs" title="Edit categories, items & pricing (saves for everyone)" onClick={() => openEditor(t)}>Edit</button>
          <button className="btn xs" title="Rename for everyone" onClick={() => { setRenaming(t.id); setRenameVal(t.name); }}>✎</button>
          {confirmDel === t.id ? (
            <button className="sowdel confirm" title="This deletes it for the whole team" onClick={() => delShared(t.id)}>sure?</button>
          ) : (
            <button className="sowdel" title="Delete for the whole team" onClick={() => setConfirmDel(t.id)}>×</button>
          )}
        </>
      ) : null}
      {kind === "device" ? (
        <>
          {onSaveScopeTpls ? <button className="btn xs" title="Move this template to the shared org library (v1 sees it too)" onClick={() => void shareUp(t)}>↑ Share</button> : null}
          <button className="sowdel" title="Delete template" onClick={() => void del(t.id)}>×</button>
        </>
      ) : null}
    </div>
  );

  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal sowmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead">
            <h3>Scope templates</h3>
            <button className="close" onClick={onClose}>×</button>
          </div>

          <div className="sowtplsave">
            <input
              placeholder="Save this scope as a template…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
            />
            <button className="btn primary" disabled={!name.trim() || !selItemCount} onClick={() => void save()}>Save</button>
          </div>
          {itemCount && saveCats.length > 1 ? (
            <div className="sowtplpick">
              <span className="sowtplpicklbl">Include categories:</span>
              {saveCats.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={"sowtplpickchip" + (catSelected(c.id) ? " on" : "")}
                  onClick={() => toggleCat(c.id)}
                  title={catSelected(c.id) ? "Included — click to leave out" : "Left out — click to include"}
                >
                  {catSelected(c.id) ? "✓ " : ""}{c.name}
                </button>
              ))}
              <span className="sowtplpickn">{selItemCount} item{selItemCount === 1 ? "" : "s"}</span>
            </div>
          ) : null}
          {!itemCount ? <p className="sownote">Add some items first — an empty scope can't be saved as a template.</p> : null}

          <input
            className="sowtplsearch"
            placeholder="Search templates by name or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {selected ? (
            <div className="sowtplpreview">
              <div className="sowtplpreviewhd">
                <strong>{selected.name}</strong>
                <span className="sowtplmeta">{selected.cats.length} categor{selected.cats.length === 1 ? "y" : "ies"} · {tplItemCount(selected)} items</span>
                <button className="sowdel" title="Close preview" onClick={() => setSelId(null)}>×</button>
              </div>
              <div className="sowtplpreviewcats">
                {selected.cats.length ? selected.cats.map((c, i) => (
                  <div key={i} className="sowtplpreviewcat">
                    <span className="sowtplpreviewcatname">{c.name}</span>
                    <span className="sowpill">{secLabel(c.section)}</span>
                    <span className="sowtplpreviewcatn">{c.items.length} item{c.items.length === 1 ? "" : "s"}</span>
                  </div>
                )) : <div className="sownote">This template has no categories.</div>}
              </div>
              <div className="sowtplapply">
                <button className="btn primary" onClick={() => onApply(selected, "add")}>Add</button>
                <button className="btn" onClick={() => onApply(selected, "merge")}>Merge</button>
                <button className="btn danger" onClick={() => onApply(selected, "replace")}>Replace</button>
              </div>
              <p className="sownote">Add appends every category as new · Merge fills same-name categories (skipping items already there) · Replace overwrites same-name categories.</p>
            </div>
          ) : null}

          {shared.length ? (
            <div className="sowtplgroup">
              <div className="sowtplgrouphd">Team library{onSaveScopeTpls ? "" : " (read-only)"}</div>
              <div className="sowtpllist">{shared.map((t) => row(t, "shared"))}</div>
            </div>
          ) : null}

          {edId && edEntry ? (
            <div className="sowtpleditor">
              <div className="sowtplpreviewhd">
                <strong>Editing “{typeof edEntry["name"] === "string" ? (edEntry["name"] as string) : "template"}” for the whole team</strong>
                <span className="sowtplmeta">changes save on “Save template”</span>
              </div>
              {edCats().map((c, ci) => (
                <div key={ci} className="sowtpledcat">
                  <div className="sowtpledcathd">
                    <input value={typeof c["name"] === "string" ? (c["name"] as string) : ""} placeholder="Category name" onChange={(e) => edMut((cats) => { cats[ci]!["name"] = e.target.value; })} />
                    <select value={typeof c["section"] === "string" ? (c["section"] as string) : "interiors"} onChange={(e) => edMut((cats) => { cats[ci]!["section"] = e.target.value; })}>
                      {SOW_SECTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <button className="sowdel" title="Remove this category and its items" onClick={() => edMut((cats) => { cats.splice(ci, 1); })}>×</button>
                  </div>
                  {(Array.isArray(c["items"]) ? (c["items"] as Record<string, unknown>[]) : []).map((it, ii) => (
                    <div key={ii} className="sowtpleditem">
                      <input className="edname" value={typeof it["name"] === "string" ? (it["name"] as string) : ""} placeholder="Item" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["name"] = e.target.value; })} />
                      <input className="edqty" inputMode="decimal" value={String(it["qty"] ?? "")} placeholder="Qty" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["qty"] = parseFloat(e.target.value) || 0; })} />
                      <input className="edunit" value={typeof it["unit"] === "string" ? (it["unit"] as string) : ""} placeholder="each" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["unit"] = e.target.value; })} />
                      <input className="edmoney" inputMode="decimal" value={String(it["labor"] ?? "")} placeholder="Labor $" title="Labor $" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["labor"] = parseFloat(e.target.value) || 0; })} />
                      <input className="edmoney" inputMode="decimal" value={String(it["material"] ?? "")} placeholder="Mat $" title="Material $" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["material"] = parseFloat(e.target.value) || 0; })} />
                      <input className="eddesc" value={typeof it["desc"] === "string" ? (it["desc"] as string) : ""} placeholder="Description" onChange={(e) => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[])[ii]!["desc"] = e.target.value; })} />
                      <button className="sowdel" onClick={() => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[]).splice(ii, 1); })}>×</button>
                    </div>
                  ))}
                  <button className="btn xs" onClick={() => edMut((cats) => { (cats[ci]!["items"] as Record<string, unknown>[]).push({ name: "", desc: "", qty: 1, unit: "each", labor: 0, material: 0, useCombined: false, combined: 0, matName: "", matPrice: 0, laborNotes: "", materialNotes: "", matUrl: "", matImg: "", mats: [] }); })}>+ Item</button>
                </div>
              ))}
              <div className="esignsendacts">
                <button className="btn xs" onClick={() => edMut((cats) => { cats.push({ name: "New category", section: "interiors", items: [] }); })}>+ Category</button>
                <button className="btn slim primary" onClick={saveEditor}>Save template</button>
                <button className="btn slim" onClick={() => { setEdId(null); setEdEntry(null); }}>Cancel</button>
              </div>
            </div>
          ) : null}

          {device.length || busy || (!sharedTemplates.length && !onSaveScopeTpls) ? (
            <div className="sowtplgroup">
              {sharedTemplates.length || onSaveScopeTpls ? <div className="sowtplgrouphd">On this device only</div> : null}
              <div className="sowtpllist">
                {busy ? (
                  <div className="sownote">Loading…</div>
                ) : device.length === 0 ? (
                  <div className="sownote">{q ? "No templates match your search." : onSaveScopeTpls ? "" : "No device templates yet. Build a scope, then save it here to reuse on other deals."}</div>
                ) : (
                  device.map((t) => row(t, "device"))
                )}
              </div>
            </div>
          ) : null}
          <p className="sowv1note">{onSaveScopeTpls
            ? "Saves go to the team library — everyone (v1 included) sees them. ↑ Share moves an old device-only template to the team."
            : "Preview a template, then choose Add, Merge, or Replace. Shared templates come from v1 (apply-only here); templates you save live on this device."}</p>

          <div className="cmpmodalacts">
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
